import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCampaigns, useCreateCampaign, useUpdateCampaign, useDeleteCampaign } from "@/hooks/use-campaigns";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useStudioHref } from "@/hooks/use-studio-href";
import { useAgentStream } from "@/hooks/use-agents";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, Target, Plus, DollarSign, BarChart3, TrendingUp, X,
  Sparkles, Trash2, Zap, Eye, MousePointerClick, Activity,
  Pause, Play, Radio, ChevronDown, ChevronUp, Megaphone,
  LayoutDashboard, Users, ExternalLink, Globe, MapPin, Calendar,
  ArrowRight, ArrowLeft, CheckCircle2, Settings2, Upload, ImageIcon,
  Rocket, Bot, Pencil, PauseCircle, PlayCircle, AlertCircle, Save, Copy, Video
} from "lucide-react";
import { SiFacebook, SiInstagram } from "react-icons/si";
import type { AdCampaign } from "@shared/schema";

const PLATFORMS = ["Meta", "Google", "LinkedIn", "X"] as const;
const STATUSES = ["ACTIVE", "PAUSED", "COMPLETED"] as const;
const TONES = ["Professional", "Casual", "Bold", "Playful"] as const;
const OBJECTIVES = ["AWARENESS", "TRAFFIC", "ENGAGEMENT", "LEADS", "SALES", "APP_PROMOTION"] as const;

const META_OBJECTIVE_MAP: Record<string, string> = {
  AWARENESS: "OUTCOME_AWARENESS",
  TRAFFIC: "OUTCOME_TRAFFIC",
  ENGAGEMENT: "OUTCOME_ENGAGEMENT",
  LEADS: "OUTCOME_LEADS",
  SALES: "OUTCOME_SALES",
  APP_PROMOTION: "OUTCOME_APP_PROMOTION",
};

const REVERSE_OBJECTIVE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(META_OBJECTIVE_MAP).map(([k, v]) => [v, k])
);

function normalizeObjectiveToUI(obj: string): string {
  if (REVERSE_OBJECTIVE_MAP[obj]) return REVERSE_OBJECTIVE_MAP[obj];
  return obj;
}

const META_CTA_OPTIONS = [
  "LEARN_MORE", "SHOP_NOW", "SIGN_UP", "GET_QUOTE", "CONTACT_US",
  "BOOK_NOW", "DOWNLOAD", "GET_OFFER", "APPLY_NOW", "SUBSCRIBE",
  "WATCH_MORE", "SEND_MESSAGE", "CALL_NOW", "GET_DIRECTIONS",
] as const;

const formatCtaLabel = (cta: string) => cta.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()).replace(/\bCta\b/i, "CTA");

const buildAdsManagerUrl = (adAccountId: string, metaCampaignId?: string) => {
  const actId = adAccountId.startsWith("act_") ? adAccountId : `act_${adAccountId}`;
  const base = `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${actId.replace("act_", "")}`;
  return metaCampaignId ? `${base}&campaign_ids=${metaCampaignId}` : base;
};

const SPECIAL_AD_CATEGORIES = [
  { value: "NONE", label: "None (standard campaign)" },
  { value: "HOUSING", label: "Housing (real estate, rentals)" },
  { value: "EMPLOYMENT", label: "Employment (jobs, recruiting)" },
  { value: "CREDIT", label: "Credit (loans, credit cards)" },
  { value: "ISSUES_ELECTIONS_POLITICS", label: "Social Issues, Elections or Politics" },
] as const;

interface FilterState {
  platform: string | null;
  status: string | null;
}

interface AdCreativeResponse {
  headline: string;
  variations: string[];
  body: string;
  cta: string;
  imagePrompt: string;
  targetingNotes: string;
  adImageUrl?: string;
}

function calculateStats(campaigns: AdCampaign[]) {
  return {
    totalBudget: campaigns.reduce((sum, c) => sum + c.budget, 0),
    totalSpent: campaigns.reduce((sum, c) => sum + c.spent, 0),
    totalConversions: campaigns.reduce((sum, c) => sum + c.conversions, 0),
    activeCampaigns: campaigns.filter(c => c.status === "ACTIVE").length,
  };
}

function filterCampaigns(campaigns: AdCampaign[], filters: FilterState) {
  return campaigns.filter(campaign => {
    if (filters.platform && campaign.platform !== filters.platform) return false;
    if (filters.status && campaign.status !== filters.status) return false;
    return true;
  });
}

function StatusBadge({ status }: { status: string }) {
  if (status === "ACTIVE") return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20">{status}</Badge>;
  if (status === "PAUSED") return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20">{status}</Badge>;
  if (status === "COMPLETED") return <Badge className="bg-muted text-muted-foreground border-border">{status}</Badge>;
  if (status === "FAILED") return <Badge className="bg-red-500/15 text-red-400 border-red-500/20">FAILED</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function PlatformBadge({ platform }: { platform: string }) {
  const colors: Record<string, string> = {
    Meta: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    Google: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
    LinkedIn: "bg-sky-500/15 text-sky-400 border-sky-500/20",
    X: "bg-muted text-muted-foreground border-border",
  };
  return <Badge className={colors[platform] || "bg-muted text-muted-foreground border-border"}>{platform}</Badge>;
}

function StatCard({ icon: Icon, label, value, subtitle }: { icon: any; label: string; value: string; subtitle?: string }) {
  return (
    <Card className="bg-card/50 border-border">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <Icon size={16} className="text-blue-400" />
          </div>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-2xl font-bold text-foreground font-display" data-testid={`stat-value-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground/70 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function ToggleChip({ label, active, onClick, testId }: { label: string; active: boolean; onClick: () => void; testId: string }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
        active
          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
          : "bg-muted/50 text-muted-foreground border border-border hover-elevate"
      }`}
    >
      {label}
    </button>
  );
}

function GreenChip({ label, active, onClick, testId, tooltip }: { label: string; active: boolean; onClick: () => void; testId: string; tooltip?: string }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      title={tooltip}
      className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
        active
          ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30"
          : "bg-muted/50 text-muted-foreground border border-border hover-elevate"
      }`}
    >
      {label}
    </button>
  );
}

export default function AdCenter() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const studioHref = useStudioHref();
  const { data: campaigns, isLoading } = useCampaigns();
  const { streamLogs } = useAgentStream();
  const [peteRunning, _setPeteRunning] = useState(false);
  const peteRunningRef = useRef(false);
  const setPeteRunning = (v: boolean) => { peteRunningRef.current = v; _setPeteRunning(v); };
  const [peteStreamLog, setPeteStreamLog] = useState<string[]>([]);
  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();
  const deleteCampaign = useDeleteCampaign();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: activeWorkspace } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const wsIntegrations = (activeWorkspace?.integrations as any) || {};
  const metaConnected = !!(wsIntegrations?.meta?.connected || wsIntegrations?.facebook?.connected);
  const igBusinessOnlyConnected = !metaConnected && !!(wsIntegrations?.instagram_business?.connected);

  const formRef = useRef<HTMLDivElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("Meta");
  const [budget, setBudget] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [adFormat, setAdFormat] = useState("");
  const [editTab, setEditTab] = useState<"campaign" | "creative" | "audience" | "placements">("campaign");
  const [editAdCopy, setEditAdCopy] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editAgeMin, setEditAgeMin] = useState("18");
  const [editAgeMax, setEditAgeMax] = useState("65");
  const [editGender, setEditGender] = useState("all");
  const [editCountries, setEditCountries] = useState("US,CA");
  const [editPlacements, setEditPlacements] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({ platform: null, status: null });

  const [showCreativeGen, setShowCreativeGen] = useState(false);
  const [creativeObjective, setCreativeObjective] = useState("");
  const [creativeAudience, setCreativeAudience] = useState("");
  const [creativeProduct, setCreativeProduct] = useState("");
  const [creativePlatform, setCreativePlatform] = useState("Meta");
  const [creativeTone, setCreativeTone] = useState("Professional");
  const [creativeResult, setCreativeResult] = useState<AdCreativeResponse | null>(null);

  const [showImportMeta, setShowImportMeta] = useState(false);
  const [metaAdAccountId, setMetaAdAccountId] = useState("");

  const { data: metaCampaignsFromMeta, isLoading: metaCampaignsLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "meta", "campaigns", metaAdAccountId],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const acctId = metaAdAccountId || "";
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/meta/campaigns${acctId ? `?adAccountId=${encodeURIComponent(acctId)}` : ""}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId && showImportMeta,
  });

  const importMetaCampaign = useMutation({
    mutationFn: async (mc: any) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/campaigns`, {
        name: mc.name,
        platform: "Meta",
        budget: mc.daily_budget ? Math.round(mc.daily_budget / 100) : null,
        status: mc.status === "ACTIVE" ? "ACTIVE" : "PAUSED",
        metaCampaignId: mc.id,
        targetAudience: "",
        adFormat: mc.objective || "",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      toast({ title: "Campaign Imported", description: "Campaign is now tracked locally." });
    },
    onError: (err: any) => {
      toast({ title: "Import Failed", description: err.message, variant: "destructive" });
    },
  });

  const [showMetaForm, setShowMetaForm] = useState(false);
  const [metaCampaignName, setMetaCampaignName] = useState("");
  const [metaObjective, setMetaObjective] = useState("TRAFFIC");
  const [metaDailyBudget, setMetaDailyBudget] = useState("5");
  const [metaMessage, setMetaMessage] = useState("");
  const [metaTargetAudience, setMetaTargetAudience] = useState("");
  const [metaLinkUrl, setMetaLinkUrl] = useState("");
  const [metaStatus, setMetaStatus] = useState("ACTIVE");
  const [metaCountries, setMetaCountries] = useState("US,CA");
  const [metaAgeMin, setMetaAgeMin] = useState("18");
  const [metaAgeMax, setMetaAgeMax] = useState("65");
  const [metaGender, setMetaGender] = useState("all");
  const [metaStartDate, setMetaStartDate] = useState("");
  const [metaEndDate, setMetaEndDate] = useState("");
  const [metaPlacements, setMetaPlacements] = useState<string[]>([]);
  const [metaFormStep, setMetaFormStep] = useState(0);
  const [aiPrefilling, setAiPrefilling] = useState(false);
  const [leadFormEnabled, setLeadFormEnabled] = useState(false);
  const [leadFormQuestions, setLeadFormQuestions] = useState<string[]>(["email", "full_name"]);
  const [leadFormPrivacyUrl, setLeadFormPrivacyUrl] = useState("");
  const [leadFormThankYouUrl, setLeadFormThankYouUrl] = useState("");
  const [leadFormCreating, setLeadFormCreating] = useState(false);
  const [leadFormPrivacyError, setLeadFormPrivacyError] = useState("");
  const [leadFormId, setLeadFormId] = useState<string | null>(null);
  const [leadFormCapabilityError, setLeadFormCapabilityError] = useState(false);
  const [insightsCampaignId, setInsightsCampaignId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"campaigns" | "analytics" | "creative">("campaigns");
  // T003: Ask Pete dialog
  const [showAskPeteDialog, setShowAskPeteDialog] = useState(false);
  const [askPeteBrief, setAskPeteBrief] = useState("");
  const [deleteConfirmCampaign, setDeleteConfirmCampaign] = useState<AdCampaign | null>(null);
  // T003: Creative Studio
  const [creativeBrief, setCreativeBrief] = useState("");
  const [creativeFormat, setCreativeFormat] = useState<"single" | "carousel" | "video_script">("single");
  const [creativeSlideCount, setCreativeSlideCount] = useState(5);
  const [creativeGenerating, setCreativeGenerating] = useState(false);
  const [creativeOutput, setCreativeOutput] = useState<{ headlines: string[]; bodyCopy: string[]; ctas: string[]; slides: { slide: number; headline: string; body: string }[]; notes: string } | null>(null);
  const [videoAdPanel, setVideoAdPanel] = useState(false);
  const [videoAdGenerating, setVideoAdGenerating] = useState(false);
  const [videoAdId, setVideoAdId] = useState<string | null>(null);
  const [videoAdStatus, setVideoAdStatus] = useState<string | null>(null);
  const [videoAdMp4, setVideoAdMp4] = useState<string | null>(null);
  const [videoAdError, setVideoAdError] = useState<string | null>(null);
  const [showFunnelPanel, setShowFunnelPanel] = useState(false);
  const [funnelGenerating, setFunnelGenerating] = useState(false);
  const [generatedFunnel, setGeneratedFunnel] = useState<{ id: number; slug: string; publicUrl: string; title: string; type: string } | null>(null);
  const [funnelBudgetError, setFunnelBudgetError] = useState(false);

  const { data: existingPages = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "pages"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/pages`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });
  const publishedPages = existingPages.filter((p: any) => p.status === "published" && (p.type === "lead_capture" || p.type === "lead_magnet"));

  const generateFunnelPage = async (funnelType: "lead_capture" | "lead_magnet") => {
    if (!activeWorkspaceId) return;
    setFunnelGenerating(true);
    setFunnelBudgetError(false);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/pages/generate-campaign-funnel`, {
        name: metaCampaignName || "Campaign",
        adCopy: metaMessage || "",
        audience: metaTargetAudience || "",
        objective: metaObjective,
        funnelType,
      });
      const data = await res.json();
      setGeneratedFunnel(data);
      setMetaLinkUrl(window.location.origin + data.publicUrl);
      setShowFunnelPanel(false);
      toast({ title: "Funnel page created", description: `Your ${funnelType === "lead_magnet" ? "lead magnet" : "lead capture"} page is live.` });
    } catch (e: any) {
      const errMsg = e?.message || "";
      let parsed: any = {};
      try {
        const jsonPart = errMsg.includes(":") ? errMsg.slice(errMsg.indexOf(":") + 1).trim() : errMsg;
        parsed = JSON.parse(jsonPart);
      } catch {}
      const isBudgetError =
        parsed.reason === "workspace_budget" ||
        parsed.reason === "user_budget" ||
        parsed.reason === "disabled" ||
        parsed.error === "AI budget exceeded" ||
        parsed.error === "AI disabled" ||
        parsed.errorCode === "AI_BUDGET_EXCEEDED";
      if (isBudgetError) {
        setFunnelBudgetError(true);
      } else {
        toast({ title: "Generation failed", description: parsed.message || "Could not generate funnel page.", variant: "destructive" });
      }
    } finally {
      setFunnelGenerating(false);
    }
  };

  useEffect(() => {
    if (!peteRunning) return;
    const latest = streamLogs[streamLogs.length - 1];
    if (!latest) return;
    if (latest.agent === "Pete") {
      const msg = latest.details || latest.type || "Working…";
      setPeteStreamLog(prev => {
        if (prev[prev.length - 1] === msg) return prev;
        return [...prev, msg];
      });
    }
    if (latest.agent === "Pete" && (latest.type === "milestone" || latest.type === "error")) {
      setPeteRunning(false);
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "meta", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      if (latest.type === "milestone") {
        setPeteStreamLog(prev => [...prev, "✓ Done — campaigns drafted"]);
        toast({ title: "Pete drafted your campaigns", description: "They're saved as drafts below. Click 'Approve & Launch' on each to go live on Meta." });
      } else {
        setPeteStreamLog(prev => [...prev, `❌ ${latest.details || "Something went wrong"}`]);
        toast({ title: "Pete had an issue", description: latest.details || "", variant: "destructive" });
      }
    }
  }, [streamLogs, peteRunning, activeWorkspaceId]);

  useEffect(() => {
    if (!peteRunning) return;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "meta", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
    }, 20000);
    const timeout = setTimeout(() => { setPeteRunning(false); clearInterval(interval); }, 300000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [peteRunning, activeWorkspaceId]);

  const [metaGeoMode, setMetaGeoMode] = useState<"country" | "local">("country");
  const [metaCities, setMetaCities] = useState("");
  const [metaProvinces, setMetaProvinces] = useState("");
  const [metaRadius, setMetaRadius] = useState("10");
  const [metaZips, setMetaZips] = useState("");
  const [metaBehaviors, setMetaBehaviors] = useState<string[]>([]);
  const [metaCreativeMode, setMetaCreativeMode] = useState<"none" | "image_url" | "image_upload" | "video_url">("none");
  const [metaImageUrl, setMetaImageUrl] = useState("");
  const [metaImageData, setMetaImageData] = useState("");
  const [metaImagePreview, setMetaImagePreview] = useState("");
  const [metaVideoUrl, setMetaVideoUrl] = useState("");
  const [metaHeadline, setMetaHeadline] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaDisplayUrl, setMetaDisplayUrl] = useState("");
  const [metaCta, setMetaCta] = useState("LEARN_MORE");
  const [metaPixelId, setMetaPixelId] = useState("");
  const [metaCustomAudienceId, setMetaCustomAudienceId] = useState("");
  const [metaSpecialAdCategory, setMetaSpecialAdCategory] = useState("NONE");
  const [metaAdvantageAudience, setMetaAdvantageAudience] = useState(false);
  const [metaAdvantageShopping, setMetaAdvantageShopping] = useState(false);
  const [metaAutomaticPlacements, setMetaAutomaticPlacements] = useState(false);
  const [launchLog, setLaunchLog] = useState<string[]>([]);
  const launchLogRef = useRef<HTMLDivElement>(null);
  const prefillAbortRef = useRef<AbortController | null>(null);
  const [adPreviewTab, setAdPreviewTab] = useState("fb_feed");
  const imageInputRef = useRef<HTMLInputElement>(null);

  const { data: adAccounts, isLoading: adAccountsLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "meta", "ad-accounts"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/meta/ad-accounts`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  useEffect(() => {
    if (adAccounts && adAccounts.length > 0 && !metaAdAccountId) {
      setMetaAdAccountId(adAccounts[0].id);
    }
  }, [adAccounts]);

  // F8: Pre-fill geo targeting, link URL, placements, and privacy URL from workspace when form opens
  useEffect(() => {
    if (launchLogRef.current) {
      launchLogRef.current.scrollTop = launchLogRef.current.scrollHeight;
    }
  }, [launchLog]);

  useEffect(() => {
    if (!showMetaForm || editingCampaignId) return;
    const ci = activeWorkspace?.contactInfo || {};
    if (ci.city && !metaCities) {
      setMetaCities(ci.city);
      setMetaGeoMode("local");
    }
    if (!metaLinkUrl) {
      const wsUrl = ci.website || activeWorkspace?.url || "";
      if (wsUrl) setMetaLinkUrl(wsUrl.startsWith("http") ? wsUrl : `https://${wsUrl}`);
    }
    if (metaPlacements.length === 0) {
      setMetaPlacements(["facebook_feed", "instagram_feed", "instagram_stories"]);
    }
    if (!leadFormPrivacyUrl) {
      const wsUrl = ci.website || activeWorkspace?.url || "";
      if (wsUrl) {
        const base = wsUrl.startsWith("http") ? wsUrl : `https://${wsUrl}`;
        setLeadFormPrivacyUrl(base.replace(/\/$/, "") + "/privacy");
      }
    }
    if (ci.country && !metaCountries) {
      setMetaCountries(ci.country.toUpperCase());
    }
  }, [showMetaForm, activeWorkspace]);

  const createMetaCampaign = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/meta/campaigns`, data);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meta Campaign Created", description: "Your campaign is live on Meta Ads." });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      setShowMetaForm(false);
      setMetaCampaignName("");
      setMetaDailyBudget("");
      setMetaMessage("");
      setMetaTargetAudience("");
      setMetaLinkUrl("");
      setMetaCountries("US,CA");
      setMetaAgeMin("18");
      setMetaAgeMax("65");
      setMetaGender("all");
      setMetaStartDate("");
      setMetaEndDate("");
      setMetaPlacements([]);
      setLeadFormEnabled(false);
      setLeadFormQuestions(["email", "full_name"]);
      setLeadFormPrivacyUrl("");
      setLeadFormThankYouUrl("");
      setLeadFormId(null);
      setMetaFormStep(0);
      setMetaGeoMode("country");
      setMetaCities("");
      setMetaRadius("10");
      setMetaZips("");
      setMetaProvinces("");
      setMetaBehaviors([]);
      setMetaCreativeMode("none");
      setMetaImageUrl("");
      setMetaImageData("");
      setMetaImagePreview("");
      setMetaVideoUrl("");
      setMetaHeadline("");
      setMetaDescription("");
      setMetaDisplayUrl("");
      setMetaCta("LEARN_MORE");
      setMetaPixelId("");
      setMetaCustomAudienceId("");
      setMetaSpecialAdCategory("NONE");
      setMetaAdvantageAudience(false);
      setMetaAdvantageShopping(false);
      setMetaAutomaticPlacements(false);
      setLaunchLog([]);
    },
    onError: (err: any) => {
      toast({ title: "Meta Campaign Failed", description: err.message || "Could not create Meta campaign.", variant: "destructive" });
    },
  });

  const [launchingCampaignId, setLaunchingCampaignId] = useState<number | null>(null);
  const [reviewCampaign, setReviewCampaign] = useState<AdCampaign | null>(null);
  const [launchAsPaused, setLaunchAsPaused] = useState(false);

  const approveLaunch = useMutation({
    mutationFn: async ({ campaignId, asPaused }: { campaignId: number; asPaused: boolean }) => {
      setLaunchingCampaignId(campaignId);
      setLaunchLog(["Connecting to Meta Ads API…"]);

      const sseRes = await fetch(`/api/workspaces/${activeWorkspaceId}/campaigns/${campaignId}/approve-launch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
        credentials: "include",
        body: JSON.stringify({ launchAsPaused: asPaused }),
      });

      if (!sseRes.ok) {
        const errData = await sseRes.json().catch(() => ({ message: "Launch failed" }));
        throw new Error(errData.message || "Launch failed");
      }

      const reader = sseRes.body?.getReader();
      const decoder = new TextDecoder();
      let finalData: any = null;

      if (reader) {
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const parsed = JSON.parse(line.slice(6));
                if (parsed.step) {
                  setLaunchLog(prev => [...prev, parsed.step]);
                }
                if (parsed.done) {
                  finalData = parsed;
                }
              } catch {}
            }
          }
        }
      }

      if (!finalData) {
        throw new Error("Launch did not complete — no confirmation received from Meta.");
      }
      if (finalData.error) {
        throw new Error(finalData.message || "Meta rejected the campaign. Check your settings and try again.");
      }
      return finalData;
    },
    onSuccess: (data) => {
      setLaunchingCampaignId(null);
      setReviewCampaign(null);
      setLaunchLog([]);
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      const isActive = data.status === "ACTIVE";
      const metaIds = data.meta || {};
      const campaignId = metaIds.metaCampaignId || data.metaCampaignId || "";
      const adSetId = metaIds.metaAdSetId || "";
      const adId = metaIds.metaAdId || "";
      const idSummary = [
        campaignId && `Campaign: ${campaignId}`,
        adSetId && `Ad Set: ${adSetId}`,
        adId && `Ad: ${adId}`,
      ].filter(Boolean).join(" · ");
      const launchAdAccountId = data.meta?.metaAdAccountId || metaAdAccountId || "";
      const adsManagerUrl = launchAdAccountId ? buildAdsManagerUrl(launchAdAccountId, campaignId) : "https://adsmanager.facebook.com/adsmanager/manage/campaigns";
      toast({
        title: isActive ? "Campaign is LIVE on Meta!" : "Campaign saved to Meta (Paused)",
        description: isActive
          ? `${idSummary}. Running with full creative, headline, and targeting. View in Ads Manager →`
          : data.pausedBySandbox
            ? `${idSummary}. Test mode: it was saved paused and nothing is spending.`
            : `${idSummary}. Activate in Meta Ads Manager when ready. View in Ads Manager →`,
        action: (
          <a href={adsManagerUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button size="sm" variant="outline" className="text-xs">Open Ads Manager</Button>
          </a>
        ),
      });
    },
    onError: (err: any) => {
      setLaunchingCampaignId(null);
      const errMsg = err.message || "Could not launch on Meta. Ensure Meta Ads is connected.";
      setLaunchLog(prev => [...prev, `❌ Error: ${errMsg}`]);
      toast({ title: "Launch failed", description: errMsg, variant: "destructive" });
    },
  });

  const { data: campaignInsights, isLoading: insightsLoading } = useQuery<any[]>({
    queryKey: ["/api/campaigns", insightsCampaignId, "meta", "insights"],
    queryFn: async () => {
      if (!insightsCampaignId) return [];
      const res = await fetch(`/api/campaigns/${insightsCampaignId}/meta/insights`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!insightsCampaignId,
  });

  const { data: budgetEnvelopes = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "ad-budget-envelopes"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/ad-budget-envelopes`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const upsertEnvelope = useMutation({
    mutationFn: async ({ platform, weeklyLimitCents }: { platform: string; weeklyLimitCents: number }) => {
      const res = await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}/ad-budget-envelopes`, { platform, weeklyLimitCents });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "ad-budget-envelopes"] });
      toast({ title: "Budget envelope saved" });
    },
  });

  const [budgetCheckRunning, setBudgetCheckRunning] = useState(false);
  const runBudgetCheck = async () => {
    if (!activeWorkspaceId) return;
    setBudgetCheckRunning(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/agents/pete/auto-launch-check`, {});
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "ad-budget-envelopes"] });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "pete-logs"] });
      const launched = data.launched ?? 0;
      const paused = data.paused ?? 0;
      const warnings = data.warnings ?? [];
      if (launched > 0) {
        toast({ title: `Pete launched ${launched} campaign(s)`, description: `Spend logged to budget envelope. ${warnings[0] || ""}`.trim() });
      } else if (paused > 0) {
        toast({ title: `Pete paused ${paused} campaign(s)`, description: warnings[0] || "Weekly budget limits enforced." });
      } else if (warnings.length > 0) {
        toast({ title: "Budget notice", description: warnings[0] });
      } else {
        toast({ title: "Budget check complete", description: "All envelopes are within limits." });
      }
    } catch (e: any) {
      toast({ title: "Budget check failed", description: e.message || "Could not run check.", variant: "destructive" });
    } finally {
      setBudgetCheckRunning(false);
    }
  };

  const { data: peteLogs = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "pete-logs"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/logs`, { credentials: "include" });
      if (!res.ok) return [];
      const all: any[] = await res.json();
      return all.filter((l: any) => l.agent === "pete" && ["CAMPAIGN_LAUNCHED","BUDGET_EXHAUSTED","BUDGET_WARNING","BUDGET_BLOCKED_LAUNCH"].includes(l.action)).slice(0, 10);
    },
    enabled: !!activeWorkspaceId && activeTab === "analytics",
  });

  const [envelopeModal, setEnvelopeModal] = useState<{ platform: string; label: string; currentCents: number } | null>(null);
  const [envelopeInput, setEnvelopeInput] = useState("");

  const toggleMetaStatus = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiRequest("PATCH", `/api/campaigns/${id}/meta/status`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      toast({ title: "Campaign Updated", description: "Meta campaign status updated." });
    },
    onError: (err: any) => {
      // apiRequest errors read "<status>: <body>"; show the server's sentence (e.g. why activation was refused), not raw JSON
      let description = err.message || "Failed to update.";
      try { description = JSON.parse(String(err.message).replace(/^\d+:\s*/, "")).message || description; } catch { /* plain text */ }
      toast({ title: "Update Failed", description, variant: "destructive" });
    },
  });

  const generateCreative = useMutation({
    mutationFn: async (data: {
      platform: string;
      objective: string;
      targetAudience: string;
      product: string;
      tone: string;
      workspaceId: number;
    }) => {
      const res = await apiRequest("POST", "/api/ai/ad-creative", data);
      return res.json();
    },
    onSuccess: (data) => setCreativeResult(data),
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message || "Could not generate ad creative.", variant: "destructive" });
    },
  });

  const addLeadForm = useMutation({
    mutationFn: async (data: { privacyPolicyUrl: string; questions: string[] }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/campaigns/${editingCampaignId}/add-lead-form`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
      toast({ title: "Lead Form Added", description: "Your campaign now has a lead generation form." });
      setEditingCampaignId(null);
    },
    onError: (err: any) => {
      toast({ title: "Failed to add form", description: err.message, variant: "destructive" });
    },
  });

  function handleCreate() {
    if (!name.trim() || !budget || !activeWorkspaceId) return;
    if (editingCampaignId) {
      const editingCampaign = campaigns?.find((c: any) => c.id === editingCampaignId);
      const isMetaCampaign = !!(editingCampaign as any)?.metaCampaignId;
      updateCampaign.mutate(
        {
          id: editingCampaignId,
          name: name.trim(),
          platform,
          budget: parseFloat(budget),
          dailyBudget: parseFloat(budget),
          targetAudience: targetAudience.trim() || undefined,
          adFormat: adFormat.trim() || undefined,
          adCopy: editAdCopy.trim() || undefined,
          startDate: editStartDate || undefined,
          endDate: editEndDate || undefined,
          creativeUrl: editImageUrl || undefined,
        },
        {
          onSuccess: async () => {
            if (isMetaCampaign) {
              const syncErrors: string[] = [];
              try {
                await apiRequest("PATCH", `/api/campaigns/${editingCampaignId}/meta/fields`, {
                  name: name.trim(),
                  dailyBudgetCents: Math.round(parseFloat(budget) * 100),
                  startDate: editStartDate || null,
                  endDate: editEndDate || null,
                });
              } catch { syncErrors.push("campaign fields"); }
              if ((editingCampaign as any)?.metaAdId && editAdCopy.trim()) {
                try {
                  await apiRequest("PATCH", `/api/campaigns/${editingCampaignId}/meta/creative`, {
                    message: editAdCopy.trim(),
                    imageUrl: editImageUrl || undefined,
                  });
                } catch { syncErrors.push("creative"); }
              }
              if ((editingCampaign as any)?.metaAdSetId) {
                const genderMap: Record<string, number[]> = { male: [1], female: [2], all: [0] };
                const countries = editCountries.split(",").map(c => c.trim()).filter(Boolean);
                try {
                  await apiRequest("PATCH", `/api/campaigns/${editingCampaignId}/meta/adset`, {
                    ageMin: parseInt(editAgeMin),
                    ageMax: parseInt(editAgeMax),
                    genders: genderMap[editGender] || [0],
                    countries: countries.length > 0 ? countries : undefined,
                    placements: editPlacements.length > 0 ? editPlacements : undefined,
                  });
                } catch { syncErrors.push("audience/placements"); }
              }
              if (syncErrors.length === 0) {
                toast({ title: "Campaign Updated", description: "Saved locally and synced to Meta." });
              } else {
                toast({ title: "Campaign Updated", description: `Saved locally. Some Meta syncs failed: ${syncErrors.join(", ")}. Check your Meta connection.`, variant: "default" });
              }
            } else {
              toast({ title: "Campaign Updated", description: "Campaign details saved." });
            }
            resetForm();
          },
          onError: (err: any) => {
            toast({ title: "Update Failed", description: err.message || "Could not update campaign.", variant: "destructive" });
          },
        }
      );
    } else {
      createCampaign.mutate(
        {
          workspaceId: activeWorkspaceId,
          name: name.trim(),
          platform,
          budget: parseFloat(budget),
          status: "ACTIVE",
          targetAudience: targetAudience.trim() || undefined,
          adFormat: adFormat.trim() || undefined,
        },
        {
          onSuccess: () => {
            resetForm();
          },
          onError: (err: any) => {
            toast({ title: "Campaign Failed", description: err.message || "Could not create campaign.", variant: "destructive" });
          },
        }
      );
    }
  }

  function handleStatusChange(campaign: AdCampaign, newStatus: string) {
    updateCampaign.mutate(
      { id: campaign.id, status: newStatus },
      { onError: (err: any) => toast({ title: "Update Failed", description: err.message, variant: "destructive" }) }
    );
  }

  function openCampaignBuilder(campaign?: AdCampaign) {
    if (campaign) {
      setEditingCampaignId(campaign.id);
      setMetaCampaignName(campaign.name);
      setMetaDailyBudget(String((campaign as any).dailyBudget || campaign.budget || ""));
      setMetaTargetAudience(campaign.targetAudience || "");
      const meta = (campaign as any).campaignMetadata || {};
      setMetaObjective(normalizeObjectiveToUI(meta.objective || (campaign as any).adFormat || "LEADS"));
      setMetaLinkUrl(meta.linkUrl || "");
      setMetaAgeMin(String(meta.ageMin || "18"));
      setMetaAgeMax(String(meta.ageMax || "65"));
      const genders = meta.genders || [];
      setMetaGender(genders.length === 0 || genders.includes(0) ? "all" : genders[0] === 1 ? "male" : "female");
      const countries = meta.countries || [];
      const cities = meta.cities || [];
      if (countries.length > 0) { setMetaGeoMode("country"); setMetaCountries(countries.join(",")); }
      else if (cities.length > 0) { setMetaGeoMode("local"); setMetaCities(Array.isArray(cities) ? cities.join(",") : cities); }
      else { setMetaGeoMode("country"); setMetaCountries("US,CA"); }
      setMetaZips((meta.zips || []).join(",") || "");
      setMetaProvinces((meta.provinces || []).join(",") || "");
      setMetaPlacements(meta.placements || []);
      setMetaBehaviors(meta.behaviors || []);
      setMetaCta(meta.callToAction || "LEARN_MORE");
      setMetaPixelId(meta.pixelId || "");
      setMetaCustomAudienceId(meta.customAudienceId || "");
      setMetaSpecialAdCategory(meta.specialAdCategory || "NONE");
      setMetaAdvantageAudience(!!meta.advantageAudience);
      setMetaAdvantageShopping(!!meta.advantageShopping);
      setMetaAutomaticPlacements(!!meta.automaticPlacements);
      setMetaDisplayUrl(meta.displayUrl || "");
      setMetaStartDate((campaign as any).startDate || "");
      setMetaEndDate((campaign as any).endDate || "");
      const creativeUrl = (campaign as any).creativeUrl || "";
      if (creativeUrl && creativeUrl.startsWith("data:image")) {
        // Pete-generated base64 image — show in preview but don't put in URL field
        setMetaCreativeMode("none");
        setMetaImageUrl("");
        setMetaImagePreview(creativeUrl);
      } else if (creativeUrl) {
        setMetaCreativeMode("image_url");
        setMetaImageUrl(creativeUrl);
        setMetaImagePreview(creativeUrl);
      } else {
        setMetaCreativeMode("none");
        setMetaImageUrl("");
        setMetaImagePreview("");
      }
      try {
        const rawCopy = (campaign as any).adCopy;
        if (rawCopy) {
          const parsed = JSON.parse(rawCopy);
          setMetaMessage(parsed.bodyText || parsed.body || rawCopy);
          if (parsed.headline) setMetaHeadline(parsed.headline);
          if (parsed.cta) setMetaCta(parsed.cta);
        } else { setMetaMessage(""); }
      } catch { setMetaMessage((campaign as any).adCopy || ""); }
      if (!metaHeadline && meta.headline) setMetaHeadline(meta.headline);
    } else {
      setEditingCampaignId(null);
      setMetaCampaignName(""); setMetaDailyBudget("5"); setMetaTargetAudience("");
      setMetaObjective("LEADS"); setMetaLinkUrl(""); setMetaAgeMin("18"); setMetaAgeMax("65");
      setShowFunnelPanel(false); setGeneratedFunnel(null); setFunnelBudgetError(false);
      setMetaGender("all"); setMetaGeoMode("country"); setMetaCountries("US,CA");
      setMetaCities(""); setMetaZips(""); setMetaProvinces(""); setMetaPlacements([]); setMetaBehaviors([]);
      setMetaStartDate(""); setMetaEndDate(""); setMetaCreativeMode("none");
      setMetaImageUrl(""); setMetaImageData(""); setMetaImagePreview("");
      setMetaMessage(""); setLeadFormEnabled(false);
      setLeadFormQuestions(["email", "full_name"]); setLeadFormPrivacyUrl("");
      setLeadFormThankYouUrl(""); setLeadFormId(null);
    }
    setMetaFormStep(0);
    setShowMetaForm(true);
    setShowImportMeta(false);
  }

  function resetForm() {
    setEditingCampaignId(null);
    setName("");
    setPlatform("Meta");
    setBudget("");
    setTargetAudience("");
    setAdFormat("");
    setEditAdCopy("");
    setEditStartDate("");
    setEditEndDate("");
    setEditImageUrl("");
    setEditAgeMin("18");
    setEditAgeMax("65");
    setEditGender("all");
    setEditCountries("US,CA");
    setEditPlacements([]);
    setEditTab("campaign");
    setShowForm(false);
  }

  function handleGenerateCreative() {
    if (!creativeObjective.trim() || !creativeAudience.trim() || !creativeProduct.trim() || !activeWorkspaceId) return;
    generateCreative.mutate({
      platform: creativePlatform,
      objective: creativeObjective.trim(),
      targetAudience: creativeAudience.trim(),
      product: creativeProduct.trim(),
      tone: creativeTone,
      workspaceId: activeWorkspaceId,
    });
  }

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <div className="p-4 rounded-2xl bg-card/50 border border-border mb-4">
          <Target size={32} className="text-muted-foreground/50" />
        </div>
        <h2 className="text-xl font-display font-bold text-muted-foreground">No Workspace Selected</h2>
        <p className="text-muted-foreground/70 mt-2 text-sm">Select a workspace to manage ad campaigns.</p>
      </div>
    );
  }

  const stats = campaigns && campaigns.length > 0 ? calculateStats(campaigns) : null;
  const filteredCampaigns = campaigns ? filterCampaigns(campaigns, filters) : [];

  return (
    <>
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="ad-center-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Megaphone size={20} className="text-blue-400" />
            </div>
            <h1 className="text-2xl font-display font-bold text-foreground">Ad Center</h1>
          </div>
          <p className="text-muted-foreground text-sm ml-12">Agent Pete manages and optimizes your ad campaigns.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="button-ask-pete"
            onClick={() => { setAskPeteBrief(""); setShowAskPeteDialog(true); }}
            variant="outline"
            disabled={peteRunning}
            className="gap-2 border-amber-500/30 text-amber-400"
          >
            {peteRunning ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
            Ask Pete
          </Button>
          <Button
            data-testid="button-import-meta-campaigns"
            onClick={() => setShowImportMeta(!showImportMeta)}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft size={14} />
            Import from Meta
          </Button>
          <Button
            data-testid="button-create-campaign"
            onClick={() => { setEditingCampaignId(null); setShowMetaForm(true); setShowImportMeta(false); }}
            className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500"
          >
            <Plus size={14} />
            Create Campaign
          </Button>
        </div>
      </div>

      {showImportMeta && (
        <Card className="bg-gradient-to-br from-blue-950/30 to-transparent border-blue-500/15 max-w-3xl" data-testid="import-meta-panel">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <SiFacebook size={16} className="text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-base font-bold text-foreground">Import from Meta</CardTitle>
                <p className="text-xs text-muted-foreground/70">Track your existing Meta campaigns in Drpr.ai</p>
              </div>
            </div>
            <button onClick={() => setShowImportMeta(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid="button-close-import-meta">
              <X size={16} />
            </button>
          </CardHeader>
          <CardContent className="space-y-3">
            {metaCampaignsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-6 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading your Meta campaigns...
              </div>
            ) : !metaCampaignsFromMeta || metaCampaignsFromMeta.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <SiFacebook size={32} className="text-muted-foreground/30 mx-auto" />
                <p className="text-sm text-muted-foreground">No Meta campaigns found.</p>
                <p className="text-xs text-muted-foreground/60">Make sure your Meta account is connected in <span className="text-blue-400">Settings → Connections</span> and your ad account has campaigns.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {metaCampaignsFromMeta.map((mc: any) => {
                  const alreadyTracked = campaigns?.some((c: any) => c.metaCampaignId === mc.id);
                  return (
                    <div key={mc.id} className="flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border" data-testid={`import-meta-campaign-${mc.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-foreground truncate">{mc.name}</span>
                          <Badge className={`text-[9px] shrink-0 ${mc.status === "ACTIVE" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                            {mc.status}
                          </Badge>
                          {alreadyTracked && (
                            <Badge className="text-[9px] bg-blue-500/15 text-blue-400 shrink-0" data-testid={`badge-already-tracked-${mc.id}`}>Already tracked</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground/60">
                          {mc.daily_budget && <span>${(mc.daily_budget / 100).toFixed(2)}/day</span>}
                          {mc.objective && <span>{mc.objective}</span>}
                        </div>
                      </div>
                      {!alreadyTracked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0 text-xs gap-1.5"
                          data-testid={`button-track-meta-campaign-${mc.id}`}
                          disabled={importMetaCampaign.isPending}
                          onClick={() => importMetaCampaign.mutate(mc)}
                        >
                          {importMetaCampaign.isPending ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                          Track
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showMetaForm && (() => {
        const META_STEPS = [
          { label: "Campaign", icon: Radio },
          { label: "Audience", icon: Users },
          { label: "Ad Creative", icon: Sparkles },
          { label: "Review", icon: CheckCircle2 },
        ];
        const COUNTRY_OPTIONS = [
          { code: "US", name: "United States" }, { code: "CA", name: "Canada" }, { code: "GB", name: "United Kingdom" },
          { code: "AU", name: "Australia" }, { code: "DE", name: "Germany" }, { code: "FR", name: "France" },
          { code: "IN", name: "India" }, { code: "BR", name: "Brazil" }, { code: "MX", name: "Mexico" },
          { code: "JP", name: "Japan" }, { code: "AE", name: "UAE" }, { code: "SA", name: "Saudi Arabia" },
          { code: "NG", name: "Nigeria" }, { code: "ZA", name: "South Africa" }, { code: "SG", name: "Singapore" },
        ];
        const selectedCountries = metaCountries.split(",").map(c => c.trim()).filter(Boolean);
        const toggleCountry = (code: string) => {
          if (selectedCountries.includes(code)) {
            setMetaCountries(selectedCountries.filter(c => c !== code).join(","));
          } else {
            setMetaCountries([...selectedCountries, code].join(","));
          }
        };
        const gendersPayload = metaGender === "male" ? [1] : metaGender === "female" ? [2] : undefined;

        const budgetNum = parseFloat(metaDailyBudget);
        const budgetTooLow = metaDailyBudget !== "" && (!isNaN(budgetNum) && budgetNum < 5);
        // T003: metaAdAccountId no longer required to proceed — campaign can be drafted without Meta connected
        const canProceedStep0 = metaCampaignName.trim() && metaDailyBudget && !budgetTooLow;
        const canProceedStep1 = metaGeoMode === "country" ? selectedCountries.length > 0 : (metaCities.trim().length > 0 || metaZips.trim().length > 0);
        const canProceedStep2 = metaMessage.trim() && (!leadFormEnabled || (leadFormPrivacyUrl.trim() && leadFormQuestions.length > 0));

        return (
        <Card className="bg-gradient-to-br from-emerald-950/30 to-transparent border-emerald-500/15 max-w-3xl" data-testid="meta-campaign-form">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Radio size={16} className="text-emerald-400" />
              </div>
              <div>
                <CardTitle className="text-base font-display text-foreground">{editingCampaignId ? "Edit Campaign" : "Campaign Builder"}</CardTitle>
                <p className="text-xs text-muted-foreground/70 mt-0.5">{editingCampaignId ? "Update your campaign details and sync to Meta" : "Build and launch a paid ad campaign — Meta, Google, and more"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[10px]">REAL ADS</Badge>
              <Button size="icon" variant="ghost" onClick={() => { setShowMetaForm(false); setMetaFormStep(0); if (prefillAbortRef.current) prefillAbortRef.current.abort(); }} data-testid="button-close-meta-form">
                <X size={16} />
              </Button>
            </div>
          </CardHeader>

          <div className="px-6 pb-3">
            <div className="flex items-center gap-1">
              {META_STEPS.map((step, i) => {
                const StepIcon = step.icon;
                const isActive = metaFormStep === i;
                const isDone = metaFormStep > i;
                return (
                  <div key={i} className="flex items-center gap-1 flex-1">
                    <button
                      onClick={() => { if (isDone) setMetaFormStep(i); }}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-all ${
                        isActive ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                        : isDone ? "bg-muted text-emerald-400/70 border border-border"
                        : "bg-card/50 text-muted-foreground/60 border border-border"
                      }`}
                      data-testid={`meta-step-${i}`}
                    >
                      {isDone ? <CheckCircle2 size={12} /> : <StepIcon size={12} />}
                      <span className="hidden sm:inline">{step.label}</span>
                    </button>
                    {i < META_STEPS.length - 1 && <div className={`flex-1 h-px ${isDone ? "bg-emerald-500/30" : "bg-muted"}`} />}
                  </div>
                );
              })}
            </div>
          </div>

          <CardContent className="space-y-4 pt-0">
            {metaFormStep === 0 && (
              <button
                data-testid="button-ai-prefill"
                onClick={async () => {
                  if (!activeWorkspaceId) return;
                  setAiPrefilling(true);
                  if (prefillAbortRef.current) prefillAbortRef.current.abort();
                  const abortCtrl = new AbortController();
                  prefillAbortRef.current = abortCtrl;
                  try {
                    await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/orchestrate-pete`, { task: "draft_campaigns" });
                    setPeteRunning(true);
                    setPeteStreamLog(["Pete is analyzing your strategy and drafting campaigns…"]);
                    toast({ title: "Pete is drafting campaigns", description: "Form will auto-fill when Pete finishes. Watch the live stream above." });
                    const pollStartedAt = Date.now();
                    const pollForDraft = async () => {
                      for (let i = 0; i < 30; i++) {
                        await new Promise(r => setTimeout(r, 5000));
                        if (abortCtrl.signal.aborted) { setAiPrefilling(false); return; }
                        const cRes = await fetch(`/api/workspaces/${activeWorkspaceId}/campaigns`, { credentials: "include" });
                        if (cRes.ok) {
                          const allCampaigns = await cRes.json();
                          const draft = allCampaigns
                            .filter((c: any) => (c.status === "DRAFT" || c.status === "draft") && c.platform === "Meta")
                            .sort((a: any, b: any) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
                            .find((c: any) => !c.createdAt || new Date(c.createdAt).getTime() > pollStartedAt - 60000);
                          if (draft) {
                            if (draft.name) setMetaCampaignName(draft.name);
                            if (draft.objective) setMetaObjective(normalizeObjectiveToUI(draft.objective));
                            if (draft.dailyBudget) setMetaDailyBudget(String(draft.dailyBudget));
                            if (draft.adCopy) {
                              try {
                                const parsed = typeof draft.adCopy === "string" ? JSON.parse(draft.adCopy) : draft.adCopy;
                                if (parsed.headline) setMetaHeadline(parsed.headline);
                                if (parsed.bodyText) setMetaMessage(parsed.bodyText);
                                if (parsed.cta) setMetaCta(parsed.cta);
                              } catch {
                                setMetaMessage(String(draft.adCopy));
                              }
                            }
                            if (draft.targetAudience) setMetaTargetAudience(draft.targetAudience);
                            if (draft.creativeUrl) {
                              setMetaImageUrl(draft.creativeUrl);
                              setMetaCreativeMode("image_url");
                              setMetaImagePreview(draft.creativeUrl);
                            }
                            const meta = draft.campaignMetadata || {};
                            if (meta.linkUrl) setMetaLinkUrl(meta.linkUrl);
                            toast({ title: "Pete filled in the campaign", description: "Review and adjust the details, then launch when ready." });
                            setAiPrefilling(false);
                            return;
                          }
                        }
                      }
                      const campRes = await fetch(`/api/workspaces/${activeWorkspaceId}/meta/campaign-suggestion`, { credentials: "include" });
                      if (campRes.ok) {
                        const data = await campRes.json();
                        if (data.campaignName) setMetaCampaignName(data.campaignName);
                        if (data.objective) setMetaObjective(normalizeObjectiveToUI(data.objective));
                        if (data.dailyBudget) setMetaDailyBudget(data.dailyBudget);
                        if (data.adCopy) setMetaMessage(data.adCopy);
                        if (data.targetAudience) setMetaTargetAudience(data.targetAudience);
                        if (data.linkUrl) setMetaLinkUrl(data.linkUrl);
                        toast({ title: "Pete filled in the campaign", description: "Review and adjust the details, then launch when ready." });
                      }
                      setAiPrefilling(false);
                    };
                    pollForDraft().catch((pollErr) => {
                      console.warn("[AI Prefill] polling error:", pollErr);
                      if (!abortCtrl.signal.aborted) {
                        setAiPrefilling(false);
                      }
                    });
                    return;
                  } catch (e: any) {
                    const msg = e?.message || "";
                    if (msg.includes("already active") || msg.startsWith("429")) {
                      toast({ title: "Pete is already working", description: "Wait for Pete to finish, then try again." });
                    } else {
                      toast({ title: "Auto-fill failed", description: "Could not generate campaign suggestion. Try again.", variant: "destructive" });
                    }
                  }
                  setAiPrefilling(false);
                }}
                disabled={aiPrefilling}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-blue-600/10 to-emerald-600/10 border border-blue-500/15 hover-elevate transition-all text-left"
              >
                {aiPrefilling ? <Loader2 size={18} className="text-blue-400 animate-spin" /> : <Sparkles size={18} className="text-blue-400" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{editingCampaignId ? "Re-fill with Pete's AI suggestions" : "Let Pete build this campaign"}</p>
                  <p className="text-[11px] text-muted-foreground/70">Auto-fills everything from your marketing strategy</p>
                </div>
                <ArrowRight size={14} className="text-muted-foreground/50" />
              </button>
            )}

            {metaFormStep === 0 && (
              <div className="space-y-4" data-testid="meta-step-campaign">
                {adAccountsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
                    <Loader2 size={14} className="animate-spin" /> Loading ad accounts...
                  </div>
                ) : adAccounts && adAccounts.length > 0 ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ad Account</label>
                    <select
                      data-testid="select-meta-ad-account"
                      value={metaAdAccountId}
                      onChange={(e) => setMetaAdAccountId(e.target.value)}
                      className="w-full bg-muted/50 border border-border focus:border-emerald-500/50 outline-none rounded-md px-4 py-2.5 text-sm text-foreground"
                    >
                      {adAccounts.map((acc: any) => (
                        <option key={acc.id} value={acc.id} className="bg-background text-foreground">
                          {acc.name || acc.account_id} ({acc.currency})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-md p-3 text-sm text-red-300">
                    No ad accounts found. Connect Meta with ads permissions in Settings.
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Campaign Name</label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="input-meta-campaign-name"
                      value={metaCampaignName}
                      onChange={(e) => setMetaCampaignName(e.target.value)}
                      placeholder="e.g., Spring Sale 2026"
                      className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 flex-1"
                    />
                    <button
                      type="button"
                      data-testid="button-auto-name-campaign"
                      onClick={() => {
                        const wsName = (activeWorkspace as any)?.name || "Campaign";
                        const date = new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" });
                        setMetaCampaignName(`${wsName} · ${metaObjective} · ${date}`);
                      }}
                      className="shrink-0 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:border-blue-500/40 transition-colors"
                      title="Auto-generate name from business + objective + date"
                    >
                      <Sparkles size={12} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Objective</label>
                  <div className="flex flex-wrap gap-2">
                    {OBJECTIVES.map((obj) => {
                      const tooltips: Record<string, string> = {
                        AWARENESS: "Maximize reach and brand recognition",
                        TRAFFIC: "Drive visitors to your website or app",
                        ENGAGEMENT: "Get more likes, comments, and shares",
                        LEADS: "Collect leads with forms on Facebook/Instagram",
                        SALES: "Drive purchases and conversions on your site",
                        APP_PROMOTION: "Drive app installs and in-app actions",
                      };
                      return <GreenChip key={obj} label={obj} active={metaObjective === obj} onClick={() => { setMetaObjective(obj); if (obj !== "SALES") setMetaAdvantageShopping(false); }} testId={`button-meta-objective-${obj.toLowerCase()}`} tooltip={tooltips[obj]} />;
                    })}
                  </div>
                  <p className="text-[10px] text-muted-foreground/50 mt-1">
                    {metaObjective === "AWARENESS" && `Meta API: OUTCOME_AWARENESS — Maximize reach and brand recognition`}
                    {metaObjective === "TRAFFIC" && `Meta API: OUTCOME_TRAFFIC — Drive visitors to your website or app`}
                    {metaObjective === "ENGAGEMENT" && `Meta API: OUTCOME_ENGAGEMENT — Get more likes, comments, and shares`}
                    {metaObjective === "LEADS" && `Meta API: OUTCOME_LEADS — Collect leads with forms on Facebook/Instagram`}
                    {metaObjective === "SALES" && `Meta API: OUTCOME_SALES — Drive purchases and conversions on your site`}
                    {metaObjective === "APP_PROMOTION" && `Meta API: OUTCOME_APP_PROMOTION — Drive app installs and in-app actions`}
                  </p>
                  {metaObjective === "SALES" && (
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/15">
                      <div>
                        <span className="text-xs font-semibold text-foreground">Advantage+ Shopping</span>
                        <p className="text-[10px] text-muted-foreground/60 mt-0.5">Meta's AI optimizes budget across audiences — best for e-commerce</p>
                      </div>
                      <button
                        data-testid="button-advantage-shopping-toggle"
                        onClick={() => setMetaAdvantageShopping(v => !v)}
                        className={`w-9 h-5 rounded-full transition-all relative shrink-0 ${metaAdvantageShopping ? "bg-blue-500" : "bg-muted"}`}
                      >
                        <div className={`absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-all ${metaAdvantageShopping ? "left-[18px]" : "left-[3px]"}`} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Special Ad Category</label>
                  <select
                    data-testid="select-special-ad-category"
                    value={metaSpecialAdCategory}
                    onChange={(e) => setMetaSpecialAdCategory(e.target.value)}
                    className="w-full bg-muted/50 border border-border focus:border-emerald-500/50 outline-none rounded-md px-4 py-2.5 text-sm text-foreground"
                  >
                    {SPECIAL_AD_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value} className="bg-background text-foreground">{c.label}</option>
                    ))}
                  </select>
                  {metaSpecialAdCategory !== "NONE" && (
                    <p className="text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/15 rounded-lg px-2.5 py-1.5">
                      Required by Meta for {metaSpecialAdCategory.toLowerCase()} ads. Some targeting options (age, gender, location) will be restricted.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Daily Budget</label>
                    <div className="relative">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                      <Input
                        data-testid="input-meta-daily-budget"
                        type="number"
                        min="5"
                        step="1"
                        value={metaDailyBudget}
                        onChange={(e) => setMetaDailyBudget(e.target.value)}
                        placeholder="Min. $5/day"
                        className={`bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 pl-8 ${budgetTooLow ? "border-red-500/60 focus:border-red-500" : ""}`}
                      />
                    </div>
                    {budgetTooLow && (
                      <p className="text-[10px] text-red-400 font-medium">Meta requires a minimum of $5/day for this objective.</p>
                    )}
                    {metaDailyBudget && !budgetTooLow && (
                      <div className="flex gap-3">
                        <p className="text-[10px] text-muted-foreground/50">Weekly: ~${(parseFloat(metaDailyBudget) * 7).toFixed(0)}</p>
                        <p className="text-[10px] text-muted-foreground/40">Monthly: ~${(parseFloat(metaDailyBudget) * 30).toFixed(0)}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Landing URL</label>
                    <div className="relative">
                      <ExternalLink size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                      <Input
                        data-testid="input-meta-link-url"
                        value={metaLinkUrl}
                        onChange={(e) => setMetaLinkUrl(e.target.value)}
                        placeholder="https://yoursite.com"
                        className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 pl-8"
                      />
                    </div>
                    {metaLinkUrl && !metaLinkUrl.startsWith("http") && (
                      <p className="text-[10px] text-amber-400" data-testid="warning-meta-link-url">Include https:// for the best results</p>
                    )}

                    {generatedFunnel && (
                      <div className="flex items-center gap-2 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-testid="panel-generated-funnel">
                        <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-emerald-400 font-medium truncate">{generatedFunnel.title}</p>
                          <a href={generatedFunnel.publicUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-emerald-400/60 hover:text-emerald-400 underline underline-offset-2">{generatedFunnel.publicUrl}</a>
                        </div>
                        <a href={studioHref("pages", { editPage: String(generatedFunnel.id) })} className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="link-edit-in-page-builder">Edit in Page Builder</a>
                        <button type="button" onClick={() => { setGeneratedFunnel(null); setMetaLinkUrl(""); }} className="text-muted-foreground/40 hover:text-muted-foreground"><X size={12} /></button>
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setShowFunnelPanel(v => !v)}
                        disabled={funnelGenerating}
                        className="text-[11px] text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1.5 transition-colors"
                        data-testid="button-toggle-funnel-panel"
                      >
                        <Sparkles size={12} />
                        {showFunnelPanel ? "Hide funnel options" : "Generate Funnel Page with Peggy"}
                      </button>
                      {publishedPages.length > 0 && (
                        <select
                          className="text-[11px] bg-muted/50 border border-border rounded-lg px-2 py-1 text-muted-foreground focus:text-foreground focus:border-blue-500/50 outline-none"
                          value=""
                          onChange={(e) => {
                            const page = publishedPages.find((p: any) => String(p.id) === e.target.value);
                            if (page) {
                              setMetaLinkUrl(window.location.origin + `/p/${page.slug}`);
                              setGeneratedFunnel({ id: page.id, slug: page.slug, publicUrl: `/p/${page.slug}`, title: page.title, type: page.type });
                            }
                          }}
                          data-testid="select-existing-funnel-page"
                        >
                          <option value="">Use existing page...</option>
                          {publishedPages.map((p: any) => (
                            <option key={p.id} value={String(p.id)}>{p.title} (/p/{p.slug})</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {metaObjective === "LEADS" && !showFunnelPanel && !generatedFunnel && (
                      <p className="text-[10px] text-blue-400/70 bg-blue-500/5 border border-blue-500/10 rounded-lg px-2.5 py-1.5" data-testid="panel-funnel-recommendation">
                        Funnel Page recommended for lead campaigns — works on all ad platforms without special permissions.
                      </p>
                    )}

                    {showFunnelPanel && (
                      <div className="p-3 bg-muted/30 border border-border rounded-xl space-y-3" data-testid="panel-funnel-options">
                        <p className="text-[11px] text-muted-foreground font-medium">Peggy will create a page using your campaign details</p>
                        {funnelBudgetError && (
                          <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="panel-funnel-budget-error">
                            <p className="text-[11px] text-red-400 font-medium">AI budget reached — cannot generate funnel page right now.</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            disabled={funnelGenerating}
                            onClick={() => generateFunnelPage("lead_capture")}
                            className="p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-left space-y-1 disabled:opacity-50"
                            data-testid="button-generate-lead-capture"
                          >
                            <div className="flex items-center gap-1.5">
                              {funnelGenerating ? <Loader2 size={12} className="animate-spin text-blue-400" /> : <Users size={12} className="text-blue-400" />}
                              <span className="text-xs font-semibold text-foreground">Lead Capture</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground/70 leading-snug">Headline + copy + name/email form</p>
                          </button>
                          <button
                            type="button"
                            disabled={funnelGenerating}
                            onClick={() => generateFunnelPage("lead_magnet")}
                            className="p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-left space-y-1 disabled:opacity-50"
                            data-testid="button-generate-lead-magnet"
                          >
                            <div className="flex items-center gap-1.5">
                              {funnelGenerating ? <Loader2 size={12} className="animate-spin text-purple-400" /> : <Sparkles size={12} className="text-purple-400" />}
                              <span className="text-xs font-semibold text-foreground">Lead Magnet</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground/70 leading-snug">Free guide gated behind email capture</p>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {metaFormStep === 1 && (
              <div className="space-y-4" data-testid="meta-step-audience">

                {/* ── Advantage+ Audience ── */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/5 border border-blue-500/15">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-foreground">Advantage+ Audience</span>
                      <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20 text-[9px]">META AI</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">Let Meta's AI find your best audience — hides manual fields below</p>
                  </div>
                  <button
                    data-testid="button-advantage-audience-toggle"
                    onClick={() => setMetaAdvantageAudience(v => !v)}
                    className={`w-9 h-5 rounded-full transition-all relative shrink-0 ${metaAdvantageAudience ? "bg-blue-500" : "bg-muted"}`}
                  >
                    <div className={`absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-all ${metaAdvantageAudience ? "left-[18px]" : "left-[3px]"}`} />
                  </button>
                </div>

                {/* ── Custom Audience ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Users size={12} /> Custom Audience ID <span className="text-[9px] normal-case font-normal text-muted-foreground/50">(optional)</span>
                  </label>
                  <Input
                    data-testid="input-meta-custom-audience-id"
                    value={metaCustomAudienceId}
                    onChange={(e) => setMetaCustomAudienceId(e.target.value)}
                    placeholder="e.g. 23847659234"
                    className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                  />
                  <p className="text-[10px] text-muted-foreground/50">Custom audience, lookalike, or retargeting audience ID from Meta Business Manager</p>
                </div>

                {/* ── Geography ── */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <MapPin size={12} /> Geography
                  </label>
                  <div className="flex gap-2">
                    <GreenChip label="Countries" active={metaGeoMode === "country"} onClick={() => setMetaGeoMode("country")} testId="button-geo-country" />
                    <GreenChip label="Cities / Local" active={metaGeoMode === "local"} onClick={() => setMetaGeoMode("local")} testId="button-geo-local" />
                  </div>

                  {metaGeoMode === "country" && (
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex flex-wrap gap-1.5">
                          {COUNTRY_OPTIONS.map((c) => (
                            <GreenChip
                              key={c.code}
                              label={c.code}
                              active={selectedCountries.includes(c.code)}
                              onClick={() => toggleCountry(c.code)}
                              testId={`button-country-${c.code.toLowerCase()}`}
                            />
                          ))}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0 ml-2">
                          <button
                            type="button"
                            data-testid="button-select-all-countries"
                            onClick={() => setMetaCountries(COUNTRY_OPTIONS.map(c => c.code).join(","))}
                            className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                          >
                            Select all
                          </button>
                          {selectedCountries.length > 0 && (
                            <button
                              type="button"
                              data-testid="button-clear-countries"
                              onClick={() => setMetaCountries("")}
                              className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground/50">Selected: {selectedCountries.length > 0 ? selectedCountries.map(code => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(", ") : "None"}</p>
                    </div>
                  )}

                  {metaGeoMode === "local" && (
                    <div className="space-y-3 pt-1">
                      <div className="space-y-1">
                        <span className="text-[10px] text-muted-foreground/60 uppercase font-semibold">Cities</span>
                        <div className="relative">
                          <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                          <Input
                            data-testid="input-meta-cities"
                            value={metaCities}
                            onChange={(e) => setMetaCities(e.target.value)}
                            placeholder="Toronto, Vancouver, Calgary, Mississauga"
                            className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 pl-8"
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground/50">Comma-separated city names — Meta resolves these to city-level targeting</p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] text-muted-foreground/60 uppercase font-semibold">Provinces / States</span>
                        <div className="relative">
                          <Globe size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                          <Input
                            data-testid="input-meta-provinces"
                            value={metaProvinces}
                            onChange={(e) => setMetaProvinces(e.target.value)}
                            placeholder="Ontario, British Columbia, Alberta, Quebec"
                            className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 pl-8"
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground/50">Province or state names — broadens reach to the full province/state</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground/60 uppercase font-semibold">Radius around cities (km)</span>
                          <Input
                            data-testid="input-meta-radius"
                            type="number"
                            min="1"
                            max="80"
                            value={metaRadius}
                            onChange={(e) => setMetaRadius(e.target.value)}
                            placeholder="10"
                            className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                          />
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground/60 uppercase font-semibold">ZIP / Postal Codes</span>
                          <Input
                            data-testid="input-meta-zips"
                            value={metaZips}
                            onChange={(e) => setMetaZips(e.target.value)}
                            placeholder="M5H 2N2, M6K 1E4, L5B"
                            className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {metaAdvantageAudience && (
                  <div className="space-y-2">
                    <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 text-[11px] text-blue-300/70" data-testid="advantage-audience-note">
                      <strong>Advantage+ Audience is ON</strong> — Meta AI will optimize demographics, interests, and behaviors. Geography and Custom Audience are still used as starting signals.
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Audience Suggestions <span className="text-muted-foreground/40 normal-case font-normal">(optional hints for Meta AI)</span></label>
                      <textarea
                        data-testid="input-advantage-audience-suggestions"
                        value={metaTargetAudience}
                        onChange={(e) => setMetaTargetAudience(e.target.value)}
                        placeholder="e.g. Small business owners aged 30-55 interested in digital marketing, SaaS tools, and online advertising"
                        rows={3}
                        className="w-full rounded-lg bg-muted/50 border border-border focus:border-blue-500/50 text-foreground text-sm p-2.5 placeholder:text-muted-foreground/60 resize-none"
                      />
                      <p className="text-[10px] text-muted-foreground/40">Describe your ideal audience — Meta AI uses these as starting signals for Advantage+ optimization</p>
                    </div>
                  </div>
                )}

                {!metaAdvantageAudience && (
                  <>
                {/* ── Demographics ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Users size={12} /> Demographics
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] text-muted-foreground/60">Age Min</span>
                      <select
                        data-testid="select-meta-age-min"
                        value={metaAgeMin}
                        onChange={(e) => setMetaAgeMin(e.target.value)}
                        className="w-full bg-muted/50 border border-border focus:border-emerald-500/50 outline-none rounded-md px-3 py-2 text-sm text-foreground"
                      >
                        {[13, 18, 21, 25, 30, 35, 40, 45, 50, 55, 60].map(a => (
                          <option key={a} value={String(a)} className="bg-background text-foreground">{a}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-muted-foreground/60">Age Max</span>
                      <select
                        data-testid="select-meta-age-max"
                        value={metaAgeMax}
                        onChange={(e) => setMetaAgeMax(e.target.value)}
                        className="w-full bg-muted/50 border border-border focus:border-emerald-500/50 outline-none rounded-md px-3 py-2 text-sm text-foreground"
                      >
                        {[25, 30, 35, 40, 45, 50, 55, 60, 65].map(a => (
                          <option key={a} value={String(a)} className="bg-background text-foreground">{a}{a === 65 ? "+" : ""}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-muted-foreground/60">Gender</span>
                      <select
                        data-testid="select-meta-gender"
                        value={metaGender}
                        onChange={(e) => setMetaGender(e.target.value)}
                        className="w-full bg-muted/50 border border-border focus:border-emerald-500/50 outline-none rounded-md px-3 py-2 text-sm text-foreground"
                      >
                        <option value="all" className="bg-background text-foreground">All Genders</option>
                        <option value="male" className="bg-background text-foreground">Male</option>
                        <option value="female" className="bg-background text-foreground">Female</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ── Interest Targeting ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Target size={12} /> Interest Targeting
                  </label>
                  <div className="relative">
                    <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                    <Input
                      data-testid="input-meta-target-audience"
                      value={metaTargetAudience}
                      onChange={(e) => setMetaTargetAudience(e.target.value)}
                      placeholder="e.g., home renovation, digital marketing, e-commerce"
                      className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 pl-8"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground/50">Meta auto-resolves these to interest categories from their database</p>
                </div>

                {/* ── Behavior Targeting ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Activity size={12} /> Behavior Targeting <span className="text-[9px] normal-case font-normal text-muted-foreground/50">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "Engaged Shoppers", "Small Business Owners", "Frequent Travelers",
                      "Homeowners", "Parents with Children", "Tech Enthusiasts",
                      "Sports & Outdoors", "Online Grocery Buyers", "Gamers",
                      "Healthcare & Wellness", "Real Estate Interested", "Construction & Trades",
                    ].map((b) => (
                      <GreenChip
                        key={b}
                        label={b}
                        active={metaBehaviors.includes(b)}
                        onClick={() => setMetaBehaviors(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b])}
                        testId={`button-behavior-${b.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground/50">{metaBehaviors.length === 0 ? "Select behaviors Meta tracks (purchase history, online activity, etc.)" : `${metaBehaviors.length} behavior(s) selected — Meta resolves to their behavior IDs`}</p>
                </div>
                  </>
                )}

                {/* ── Automatic Placements ── */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
                  <div>
                    <span className="text-xs font-semibold text-foreground">Automatic Placements (Recommended)</span>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">Meta distributes budget across all placements to maximize results</p>
                  </div>
                  <button
                    data-testid="button-automatic-placements-toggle"
                    onClick={() => { setMetaAutomaticPlacements(v => !v); if (!metaAutomaticPlacements) setMetaPlacements([]); }}
                    className={`w-9 h-5 rounded-full transition-all relative shrink-0 ${metaAutomaticPlacements ? "bg-emerald-500" : "bg-muted"}`}
                  >
                    <div className={`absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-all ${metaAutomaticPlacements ? "left-[18px]" : "left-[3px]"}`} />
                  </button>
                </div>

                {!metaAutomaticPlacements && (
                <>
                {/* ── Placements: Facebook ── */}
                <div className="space-y-2 p-3 rounded-lg bg-blue-600/5 border border-blue-500/15">
                  <div className="flex items-center gap-2">
                    <SiFacebook size={14} className="text-blue-400" />
                    <span className="text-xs font-semibold text-foreground">Facebook Placements</span>
                    <span className="text-[9px] text-muted-foreground/50">Leave all empty for auto (recommended)</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: "facebook_feed", label: "Feed" },
                      { id: "facebook_stories", label: "Stories" },
                      { id: "facebook_reels", label: "Reels" },
                    ].map((p) => (
                      <GreenChip
                        key={p.id}
                        label={p.label}
                        active={metaPlacements.includes(p.id)}
                        onClick={() => setMetaPlacements(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                        testId={`button-placement-${p.id}`}
                      />
                    ))}
                  </div>
                </div>

                {/* ── Placements: Instagram ── */}
                <div className={`space-y-2 p-3 rounded-lg border ${igBusinessOnlyConnected ? "bg-muted/20 border-border opacity-60" : "bg-pink-600/5 border-pink-500/20"}`}>
                  <div className="flex items-center gap-2">
                    <SiInstagram size={14} className={igBusinessOnlyConnected ? "text-muted-foreground" : "text-pink-400"} />
                    <span className="text-xs font-semibold text-foreground">Instagram Placements</span>
                    {igBusinessOnlyConnected ? (
                      <span className="text-[9px] text-amber-400/80" title="Instagram ads run through Meta. Connect your Meta account in Settings.">Requires Meta — connect in Settings</span>
                    ) : (
                      <span className="text-[9px] text-muted-foreground/50">Separate from Facebook — select to target IG specifically</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: "instagram_feed", label: "IG Feed" },
                      { id: "instagram_stories", label: "IG Stories" },
                      { id: "instagram_reels", label: "IG Reels" },
                    ].map((p) => (
                      <GreenChip
                        key={p.id}
                        label={p.label}
                        active={!igBusinessOnlyConnected && metaPlacements.includes(p.id)}
                        onClick={() => {
                          if (igBusinessOnlyConnected) {
                            toast({ title: "Meta Required", description: "Instagram ads run through Meta. Connect your Meta account in Settings.", variant: "destructive" });
                            return;
                          }
                          setMetaPlacements(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]);
                        }}
                        testId={`button-placement-${p.id}`}
                      />
                    ))}
                  </div>
                  {!igBusinessOnlyConnected && (metaPlacements.includes("instagram_feed") || metaPlacements.includes("instagram_stories") || metaPlacements.includes("instagram_reels")) && (
                    <p className="text-[10px] text-pink-400/70">Your Meta-connected Instagram account will be used for these placements.</p>
                  )}
                  {igBusinessOnlyConnected && (
                    <p className="text-[10px] text-amber-400/70">Instagram ads require a Meta (Facebook) connection. <a href="/settings" className="underline hover:text-amber-300">Connect Meta in Settings →</a></p>
                  )}
                </div>
                </>
                )}

                {/* ── Schedule ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar size={12} /> Schedule (optional)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] text-muted-foreground/60">Start Date</span>
                      <Input
                        data-testid="input-meta-start-date"
                        type="date"
                        value={metaStartDate}
                        onChange={(e) => setMetaStartDate(e.target.value)}
                        className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-muted-foreground/60">End Date</span>
                      <Input
                        data-testid="input-meta-end-date"
                        type="date"
                        value={metaEndDate}
                        onChange={(e) => setMetaEndDate(e.target.value)}
                        className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground/50">Leave blank to start immediately and run continuously</p>
                </div>
              </div>
            )}

            {metaFormStep === 2 && (
              <div className="flex gap-4" data-testid="meta-step-creative">
              <div className="flex-1 min-w-0 space-y-4">

                {/* ── Ad Visual / Image ── */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <ImageIcon size={12} /> Ad Image / Visual
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <GreenChip label="No Image" active={metaCreativeMode === "none"} onClick={() => { setMetaCreativeMode("none"); setMetaImageData(""); setMetaImagePreview(""); setMetaImageUrl(""); setMetaVideoUrl(""); }} testId="button-creative-none" />
                    <GreenChip label="Image URL" active={metaCreativeMode === "image_url"} onClick={() => setMetaCreativeMode("image_url")} testId="button-creative-url" />
                    <GreenChip label="Upload File" active={metaCreativeMode === "image_upload"} onClick={() => setMetaCreativeMode("image_upload")} testId="button-creative-upload" />
                    <GreenChip label="Video URL" active={metaCreativeMode === "video_url"} onClick={() => setMetaCreativeMode("video_url")} testId="button-creative-video-url" />
                  </div>

                  {metaCreativeMode === "image_url" && (
                    <div className="space-y-2">
                      <Input
                        data-testid="input-meta-image-url"
                        value={metaImageUrl}
                        onChange={(e) => {
                          setMetaImageUrl(e.target.value);
                          setMetaImageData(e.target.value);
                          setMetaImagePreview(e.target.value);
                        }}
                        placeholder="https://example.com/ad-image.jpg"
                        className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                      />
                      {metaImagePreview && (
                        <img src={metaImagePreview} alt="Ad preview" className="max-h-40 rounded-md object-cover border border-border" onError={() => setMetaImagePreview("")} />
                      )}
                    </div>
                  )}

                  {metaCreativeMode === "image_upload" && (
                    <div className="space-y-2">
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="hidden"
                        data-testid="input-meta-image-file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            const result = ev.target?.result as string;
                            setMetaImageData(result);
                            setMetaImagePreview(result);
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                      <button
                        data-testid="button-meta-image-upload"
                        onClick={() => imageInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-muted/50 border border-border text-sm text-muted-foreground hover-elevate"
                      >
                        <Upload size={14} />
                        {metaImagePreview ? "Change Image" : "Choose Image (JPG, PNG, GIF)"}
                      </button>
                      {metaImagePreview && (
                        <img src={metaImagePreview} alt="Ad preview" className="max-h-40 rounded-md object-cover border border-border" />
                      )}
                    </div>
                  )}

                  {metaCreativeMode === "video_url" && (
                    <div className="space-y-2">
                      <Input
                        data-testid="input-meta-video-url"
                        value={metaVideoUrl}
                        onChange={(e) => setMetaVideoUrl(e.target.value)}
                        placeholder="https://example.com/ad-video.mp4"
                        className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                      />
                      <p className="text-[10px] text-muted-foreground/50">Public video URL (MP4). Meta will upload and encode it automatically. Works great for Reels — Advantage+ will add music automatically.</p>
                    </div>
                  )}

                  {metaCreativeMode !== "none" && (
                    <p className="text-[10px] text-muted-foreground/50">
                      {metaCreativeMode === "video_url"
                        ? "Reels: 1080×1920px · Feed video: 1080×1080px · Horizontal: 1920×1080px"
                        : "Recommended: 1200×628px for feed ads · 1080×1080px for square · 1080×1920px for Stories/Reels"}
                    </p>
                  )}
                </div>

                {/* ── Ad Copy ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ad Copy / Primary Text</label>
                  <Textarea
                    data-testid="input-meta-ad-message"
                    value={metaMessage}
                    onChange={(e) => setMetaMessage(e.target.value)}
                    placeholder="Write compelling ad copy that grabs attention..."
                    rows={4}
                    className={`bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 resize-none ${metaMessage.length > 125 ? "border-amber-500/50" : ""}`}
                  />
                  <div className="flex items-center justify-between">
                    <p className={`text-[10px] ${metaMessage.length > 125 ? "text-amber-400 font-medium" : "text-muted-foreground/50"}`}>
                      {metaMessage.length}/125 {metaMessage.length > 125 ? "— over limit, may be truncated" : "characters recommended"}
                    </p>
                    {metaMessage.length > 10 && (
                      <button
                        type="button"
                        onClick={() => setMetaMessage("")}
                        className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                        data-testid="button-clear-ad-copy"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Headline & Description ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Headline <span className="text-muted-foreground/40 normal-case font-normal">(optional)</span></label>
                    <Input
                      data-testid="input-meta-headline"
                      value={metaHeadline}
                      onChange={(e) => setMetaHeadline(e.target.value)}
                      placeholder="Short, punchy headline..."
                      className={`bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 ${metaHeadline.length > 40 ? "border-red-500/50" : metaHeadline.length >= 35 ? "border-amber-500/50" : ""}`}
                    />
                    <p className={`text-[10px] ${metaHeadline.length > 40 ? "text-red-400 font-medium" : metaHeadline.length >= 35 ? "text-amber-400" : "text-muted-foreground/40"}`}>{metaHeadline.length}/40 chars {metaHeadline.length > 40 ? "— over limit, Meta will truncate" : metaHeadline.length >= 35 ? "— nearing limit" : ""}</p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description <span className="text-muted-foreground/40 normal-case font-normal">(optional)</span></label>
                    <Input
                      data-testid="input-meta-description"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      placeholder="Additional details shown below headline..."
                      className={`bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 ${metaDescription.length > 30 ? "border-red-500/50" : metaDescription.length >= 25 ? "border-amber-500/50" : ""}`}
                    />
                    <p className={`text-[10px] ${metaDescription.length > 30 ? "text-red-400 font-medium" : metaDescription.length >= 25 ? "text-amber-400" : "text-muted-foreground/40"}`}>{metaDescription.length}/30 chars {metaDescription.length > 30 ? "— over limit" : metaDescription.length >= 25 ? "— nearing limit" : ""}</p>
                  </div>
                </div>

                {/* ── CTA + Display Link ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Call to Action</label>
                    <select
                      data-testid="select-meta-cta"
                      value={metaCta}
                      onChange={(e) => setMetaCta(e.target.value)}
                      className="w-full bg-muted/50 border border-border focus:border-emerald-500/50 outline-none rounded-md px-4 py-2.5 text-sm text-foreground"
                    >
                      {META_CTA_OPTIONS.map(c => (
                        <option key={c} value={c} className="bg-background text-foreground">
                          {c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-muted-foreground/40">Button shown on the ad — maps to Meta's official CTA values</p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Display Link <span className="text-muted-foreground/40 normal-case font-normal">(optional, max 30)</span></label>
                    <Input
                      data-testid="input-meta-display-url"
                      value={metaDisplayUrl}
                      onChange={(e) => setMetaDisplayUrl(e.target.value)}
                      placeholder="insightscpa.ca"
                      className={`bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60 ${metaDisplayUrl.length > 30 ? "border-red-500/50" : ""}`}
                    />
                    <p className={`text-[10px] ${metaDisplayUrl.length > 30 ? "text-red-400 font-medium" : "text-muted-foreground/40"}`}>{metaDisplayUrl.length > 0 ? `${metaDisplayUrl.length}/30 chars` : "Shown in the ad instead of the full URL"} {metaDisplayUrl.length > 30 ? "— over limit" : ""}</p>
                  </div>
                </div>

                {/* ── Pixel Tracking ── */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Activity size={12} /> Meta Pixel ID <span className="text-[9px] normal-case font-normal text-muted-foreground/50">(optional — for SALES/LEADS)</span>
                  </label>
                  <Input
                    data-testid="input-meta-pixel-id"
                    value={metaPixelId}
                    onChange={(e) => setMetaPixelId(e.target.value)}
                    placeholder="e.g. 1234567890"
                    className="bg-muted/50 border-border focus:border-emerald-500/50 text-foreground placeholder:text-muted-foreground/60"
                  />
                  <p className="text-[10px] text-muted-foreground/50">Enables conversion tracking and retargeting — find your Pixel ID in Meta Events Manager</p>
                </div>

                {metaObjective === "LEADS" && (
                  <div className="space-y-3 p-3 rounded-lg bg-blue-600/5 border border-blue-500/15">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-blue-400" />
                        <span className="text-xs font-semibold text-foreground">Facebook Instant Lead Form</span>
                        <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20 text-[9px]">HIGH CONVERSION</Badge>
                      </div>
                      <button
                        data-testid="button-toggle-lead-form"
                        onClick={() => setLeadFormEnabled(!leadFormEnabled)}
                        className={`w-9 h-5 rounded-full transition-all relative ${leadFormEnabled ? "bg-blue-500" : "bg-muted"}`}
                      >
                        <div className={`absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-all ${leadFormEnabled ? "left-[18px]" : "left-[3px]"}`} />
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground/70">Collect leads directly on Facebook — users tap a few buttons and their name, email, phone auto-fills from their profile.</p>

                    {leadFormEnabled && (
                      <div className="space-y-3 pt-1">
                        <div className="space-y-1.5">
                          <span className="text-[10px] text-muted-foreground uppercase font-semibold">Form Fields</span>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { id: "email", label: "Email" },
                              { id: "full_name", label: "Full Name" },
                              { id: "phone", label: "Phone" },
                              { id: "company", label: "Company" },
                              { id: "city", label: "City" },
                              { id: "zip", label: "Zip Code" },
                            ].map((q) => (
                              <GreenChip
                                key={q.id}
                                label={q.label}
                                active={leadFormQuestions.includes(q.id)}
                                onClick={() => {
                                  setLeadFormQuestions(prev =>
                                    prev.includes(q.id) ? prev.filter(x => x !== q.id) : [...prev, q.id]
                                  );
                                }}
                                testId={`button-leadform-field-${q.id}`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground uppercase font-semibold">Privacy Policy URL *</span>
                          <Input
                            data-testid="input-lead-form-privacy-url"
                            value={leadFormPrivacyUrl}
                            onChange={(e) => { setLeadFormPrivacyUrl(e.target.value); if (e.target.value.trim()) setLeadFormPrivacyError(""); }}
                            placeholder="https://yoursite.com/privacy"
                            className={`bg-muted/50 border-border focus:border-blue-500/50 text-foreground placeholder:text-muted-foreground/60 text-xs ${leadFormPrivacyError ? "border-red-500/60" : ""}`}
                          />
                          {leadFormPrivacyError && <p className="text-[10px] text-red-400 mt-0.5" data-testid="text-privacy-url-error">{leadFormPrivacyError}</p>}
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground uppercase font-semibold">Thank You URL (optional)</span>
                          <Input
                            data-testid="input-lead-form-thank-you-url"
                            value={leadFormThankYouUrl}
                            onChange={(e) => setLeadFormThankYouUrl(e.target.value)}
                            placeholder="https://yoursite.com/thank-you"
                            className="bg-muted/50 border-border focus:border-blue-500/50 text-foreground placeholder:text-muted-foreground/60 text-xs"
                          />
                        </div>
                        {leadFormId && (
                          <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                            <CheckCircle2 size={12} /> Lead form created: {leadFormId}
                          </div>
                        )}
                        {leadFormCapabilityError && (
                          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 space-y-2" data-testid="lead-form-capability-error">
                            <p className="text-[11px] text-amber-300 font-semibold">Lead Ads not available on your Facebook app</p>
                            <p className="text-[10px] text-amber-200/80">
                              Your Facebook app needs Lead Generation capability enabled in Meta Business Manager (App → Capabilities → Lead Ads). Until then, you can launch this as a Traffic campaign without a lead form.
                            </p>
                            <div className="flex gap-2">
                              <button
                                data-testid="button-proceed-without-lead-form"
                                onClick={() => { setLeadFormEnabled(false); setLeadFormCapabilityError(false); }}
                                className="text-[10px] font-semibold bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 rounded px-2.5 py-1 transition-colors"
                              >
                                Proceed without Lead Form
                              </button>
                              <a
                                href="https://www.facebook.com/business/help/2038339426393196"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-semibold text-blue-400 hover:text-blue-300 underline underline-offset-2 flex items-center gap-1 py-1"
                              >
                                How to enable Lead Ads ↗
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Initial Status</label>
                  <div className="flex gap-2">
                    <button
                      data-testid="button-meta-status-paused"
                      onClick={() => setMetaStatus("PAUSED")}
                      className={`px-3.5 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
                        metaStatus === "PAUSED"
                          ? "bg-amber-500/15 text-amber-400 border border-amber-500/25"
                          : "bg-muted/50 text-muted-foreground border border-border hover-elevate"
                      }`}
                    >
                      <Pause size={12} /> Paused (review first)
                    </button>
                    <button
                      data-testid="button-meta-status-active"
                      onClick={() => setMetaStatus("ACTIVE")}
                      className={`px-3.5 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
                        metaStatus === "ACTIVE"
                          ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                          : "bg-muted/50 text-muted-foreground border border-border hover-elevate"
                      }`}
                    >
                      <Play size={12} /> Active (start immediately)
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Live Ad Preview Panel ── */}
              <div className="w-56 shrink-0 space-y-3 hidden md:block" data-testid="ad-preview-panel">
                <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider flex items-center gap-1">
                  <Eye size={10} /> Live Preview
                </p>
                {(() => {
                  const previewTabs = [
                    ...(metaPlacements.length === 0 || metaPlacements.includes("facebook_feed") ? [{ id: "fb_feed", label: "FB Feed" }] : []),
                    ...(metaPlacements.includes("facebook_stories") ? [{ id: "fb_stories", label: "FB Stories" }] : []),
                    ...(metaPlacements.includes("facebook_reels") ? [{ id: "fb_reels", label: "FB Reels" }] : []),
                    ...(metaPlacements.includes("instagram_feed") ? [{ id: "ig_feed", label: "IG Feed" }] : []),
                    ...(metaPlacements.includes("instagram_stories") ? [{ id: "ig_stories", label: "IG Stories" }] : []),
                    ...(metaPlacements.includes("instagram_reels") ? [{ id: "ig_reels", label: "IG Reels" }] : []),
                  ];
                  const activeTab = previewTabs.find(t => t.id === adPreviewTab) ? adPreviewTab : previewTabs[0]?.id || "fb_feed";
                  const copyText = metaMessage || "Your ad copy will appear here...";
                  const pageName = metaCampaignName || "Your Page";

                  return (
                    <>
                      <div className="flex flex-wrap gap-1">
                        {previewTabs.map(t => (
                          <button
                            key={t.id}
                            data-testid={`button-preview-tab-${t.id}`}
                            onClick={() => setAdPreviewTab(t.id)}
                            className={`px-2 py-0.5 rounded text-[9px] font-semibold transition-all ${activeTab === t.id ? "bg-blue-600/30 text-blue-300 border border-blue-500/30" : "bg-muted/30 text-muted-foreground/60 border border-border hover:text-muted-foreground"}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>

                      {(activeTab === "fb_feed") && (
                        <div className="rounded-lg overflow-hidden border border-border bg-[#18191a] text-white text-[10px]" data-testid="preview-fb-feed">
                          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-white/10">
                            <div className="w-6 h-6 rounded-full bg-blue-500/30 border border-blue-400/30 shrink-0" />
                            <div>
                              <div className="font-semibold text-[10px] leading-tight">{pageName}</div>
                              <div className="text-[8px] text-white/40">Sponsored ▾</div>
                            </div>
                          </div>
                          <div className="px-2.5 py-1.5 text-white/80 leading-snug line-clamp-2">{copyText}</div>
                          {metaImagePreview ? (
                            <img src={metaImagePreview} alt="Ad" className="w-full aspect-video object-cover" />
                          ) : (
                            <div className="w-full aspect-video bg-muted/30 flex items-center justify-center">
                              <ImageIcon size={20} className="text-muted-foreground/30" />
                            </div>
                          )}
                          <div className="px-2.5 py-1.5 bg-[#242526] border-t border-white/10 flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-[9px] truncate max-w-[100px]">{pageName}</div>
                              <div className="text-[8px] text-white/40">{(() => { const u = activeWorkspace?.contactInfo?.website || activeWorkspace?.socialUrls?.website || ""; try { return u ? new URL(u).hostname.replace(/^www\./, "") : "yoursite.com"; } catch { return u || "yoursite.com"; } })()}</div>
                            </div>
                            <div className="bg-blue-600 text-white text-[8px] font-bold px-2 py-0.5 rounded">{formatCtaLabel(metaCta || "LEARN_MORE")}</div>
                          </div>
                          <div className="flex items-center justify-around px-2 py-1.5 border-t border-white/10 text-[9px] text-white/40">
                            <span>👍 Like</span><span>💬 Comment</span><span>↗ Share</span>
                          </div>
                        </div>
                      )}

                      {(activeTab === "fb_stories" || activeTab === "ig_stories") && (
                        <div className="rounded-2xl overflow-hidden border-2 border-white/10 bg-black relative" style={{ aspectRatio: "9/16" }} data-testid={`preview-${activeTab}`}>
                          {metaImagePreview ? (
                            <img src={metaImagePreview} alt="Ad" className="absolute inset-0 w-full h-full object-cover" />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-muted/40 to-muted/10 flex items-center justify-center">
                              <ImageIcon size={24} className="text-white/20" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
                          <div className="absolute top-2 left-2 flex items-center gap-1.5">
                            <div className={`w-5 h-5 rounded-full border-2 shrink-0 ${activeTab === "ig_stories" ? "border-pink-500" : "border-blue-400"}`} style={{ background: activeTab === "ig_stories" ? "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" : "#1877f2" }} />
                            <div>
                              <div className="text-white text-[8px] font-bold leading-tight">{pageName.slice(0, 12)}</div>
                              <div className="text-white/50 text-[7px]">Sponsored</div>
                            </div>
                          </div>
                          <div className="absolute bottom-6 left-2 right-2">
                            <p className="text-white text-[9px] leading-snug line-clamp-2 drop-shadow">{copyText}</p>
                          </div>
                          <div className="absolute bottom-1 left-0 right-0 flex justify-center">
                            <span className="text-white/50 text-[8px]">{activeTab === "ig_stories" ? "See More ↑" : "Swipe Up ↑"}</span>
                          </div>
                        </div>
                      )}

                      {(activeTab === "fb_reels" || activeTab === "ig_reels") && (
                        <div className="rounded-2xl overflow-hidden border-2 border-white/10 bg-black relative" style={{ aspectRatio: "9/16" }} data-testid={`preview-${activeTab}`}>
                          {metaImagePreview ? (
                            <img src={metaImagePreview} alt="Ad" className="absolute inset-0 w-full h-full object-cover" />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-muted/40 to-muted/10 flex items-center justify-center">
                              <ImageIcon size={24} className="text-white/20" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />
                          <div className="absolute right-2 bottom-16 flex flex-col items-center gap-3 text-white/70">
                            <div className="flex flex-col items-center gap-0.5"><span className="text-base">♥</span><span className="text-[7px]">Like</span></div>
                            <div className="flex flex-col items-center gap-0.5"><span className="text-base">💬</span><span className="text-[7px]">Comment</span></div>
                            <div className="flex flex-col items-center gap-0.5"><span className="text-base">↗</span><span className="text-[7px]">Share</span></div>
                          </div>
                          <div className="absolute bottom-2 left-2 right-10">
                            <div className="text-white text-[8px] font-bold mb-0.5">{pageName.slice(0, 14)} · Sponsored</div>
                            <p className="text-white/70 text-[8px] leading-snug line-clamp-2">{copyText}</p>
                            <div className="mt-1 bg-white/20 text-white text-[8px] font-bold px-2 py-0.5 rounded inline-block">{formatCtaLabel(metaCta || "LEARN_MORE")}</div>
                          </div>
                        </div>
                      )}

                      {activeTab === "ig_feed" && (
                        <div className="rounded-lg overflow-hidden border border-white/10 bg-[#000] text-white text-[10px]" data-testid="preview-ig-feed">
                          <div className="flex items-center gap-2 px-2.5 py-2">
                            <div className="w-6 h-6 rounded-full border-2 border-pink-500 shrink-0" style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }} />
                            <div className="flex-1">
                              <div className="font-semibold text-[10px] leading-tight">{pageName.toLowerCase().replace(/\s+/g, "")}</div>
                              <div className="text-[8px] text-white/40">Sponsored</div>
                            </div>
                            <span className="text-white/40 text-base leading-none">···</span>
                          </div>
                          {metaImagePreview ? (
                            <img src={metaImagePreview} alt="Ad" className="w-full aspect-square object-cover" />
                          ) : (
                            <div className="w-full aspect-square bg-muted/30 flex items-center justify-center">
                              <ImageIcon size={20} className="text-muted-foreground/30" />
                            </div>
                          )}
                          <div className="flex items-center justify-between px-2.5 py-1.5 border-t border-white/10">
                            <div className="flex items-center gap-3 text-base">❤️ 💬 ↗</div>
                            <span className="text-base">🔖</span>
                          </div>
                          <div className="px-2.5 pb-2 text-[9px] text-white/70 line-clamp-2">{copyText}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              </div>
            )}

            {metaFormStep === 3 && (
              <div className="space-y-3" data-testid="meta-step-review">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Campaign</span>
                    <p className="text-sm text-foreground font-semibold truncate">{metaCampaignName}</p>
                  </div>
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Objective</span>
                    <p className="text-sm text-foreground font-semibold">{metaObjective}</p>
                  </div>
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Daily Budget</span>
                    <p className="text-sm text-foreground font-semibold">${metaDailyBudget}/day</p>
                  </div>
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Geography</span>
                    <p className="text-sm text-foreground font-semibold truncate">
                      {metaGeoMode === "country" ? selectedCountries.join(", ") : `${metaCities || "—"}${metaZips ? ` · ZIPs: ${metaZips}` : ""}`}
                    </p>
                  </div>
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Demographics</span>
                    <p className="text-sm text-foreground font-semibold">Age {metaAgeMin}-{metaAgeMax}{metaAgeMax === "65" ? "+" : ""}, {metaGender === "all" ? "All Genders" : metaGender}</p>
                  </div>
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Status</span>
                    <p className={`text-sm font-semibold ${metaStatus === "ACTIVE" ? "text-emerald-400" : "text-amber-400"}`}>{metaStatus}</p>
                  </div>
                </div>
                {metaTargetAudience && (
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Interest Targeting</span>
                    <p className="text-sm text-foreground">{metaTargetAudience}</p>
                  </div>
                )}
                {metaBehaviors.length > 0 && (
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Behaviors</span>
                    <p className="text-sm text-foreground">{metaBehaviors.join(", ")}</p>
                  </div>
                )}
                {metaImagePreview && (
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-2">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Ad Image</span>
                    <img src={metaImagePreview} alt="Ad creative" className="max-h-32 rounded-md object-cover border border-border" onError={() => {}} />
                  </div>
                )}
                <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                  <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Ad Copy</span>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{metaMessage}</p>
                </div>
                {leadFormEnabled && (
                  <div className="bg-blue-600/5 border border-blue-500/15 rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-blue-400/60 uppercase font-semibold">Instant Lead Form</span>
                    <p className="text-sm text-foreground">Fields: {leadFormQuestions.map(q => q.replace(/_/g, ' ')).join(", ")}</p>
                    {leadFormId && <p className="text-[10px] text-emerald-400">Form ID: {leadFormId}</p>}
                  </div>
                )}
                {metaPlacements.length > 0 && (
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Placements</span>
                    <p className="text-sm text-foreground">{metaPlacements.map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(", ")}</p>
                  </div>
                )}
                {(metaStartDate || metaEndDate) && (
                  <div className="bg-card/50 border border-border rounded-md p-3 space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Schedule</span>
                    <p className="text-sm text-foreground">{metaStartDate || "Immediately"} {metaEndDate ? `to ${metaEndDate}` : "(ongoing)"}</p>
                  </div>
                )}
              </div>
            )}

            {metaFormStep === 3 && !metaAdAccountId && !editingCampaignId && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-3" data-testid="connect-meta-callout">
                <AlertCircle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-300">Connect Meta to publish this campaign</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    For now you can save this as a draft, or{" "}
                    <a href="/settings?tab=integrations" className="text-blue-400 underline underline-offset-2 hover:text-blue-300 transition-colors">
                      connect Meta Ads in Settings →
                    </a>
                  </p>
                </div>
              </div>
            )}

            {metaFormStep === 3 && metaAdAccountId && (
              <div className="grid grid-cols-3 gap-2 p-2 rounded-lg bg-muted/20 border border-border text-[10px]">
                <div className="text-center">
                  <p className="text-muted-foreground/60">CTA Button</p>
                  <p className="font-semibold text-foreground mt-0.5">{metaCta.replace(/_/g, " ")}</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground/60">Advantage+ Audience</p>
                  <p className={`font-semibold mt-0.5 ${metaAdvantageAudience ? "text-blue-400" : "text-muted-foreground"}`}>{metaAdvantageAudience ? "ON" : "OFF"}</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground/60">Pixel Tracking</p>
                  <p className={`font-semibold mt-0.5 ${metaPixelId ? "text-emerald-400" : "text-muted-foreground"}`}>{metaPixelId ? "Set" : "Not set"}</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
              <div>
                {metaFormStep > 0 && (
                  <Button variant="ghost" onClick={() => setMetaFormStep(metaFormStep - 1)} className="gap-1.5" data-testid="button-meta-back">
                    <ArrowLeft size={14} /> Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => { setShowMetaForm(false); setMetaFormStep(0); }} data-testid="button-cancel-meta-campaign">
                  Cancel
                </Button>
                {metaFormStep < 3 ? (
                  <Button
                    onClick={() => setMetaFormStep(metaFormStep + 1)}
                    disabled={
                      (metaFormStep === 0 && !canProceedStep0) ||
                      (metaFormStep === 1 && !canProceedStep1) ||
                      (metaFormStep === 2 && !canProceedStep2)
                    }
                    className="gap-1.5 bg-emerald-600 text-white"
                    data-testid="button-meta-next"
                  >
                    Next <ArrowRight size={14} />
                  </Button>
                ) : !metaAdAccountId && !editingCampaignId ? (
                  <Button
                    data-testid="button-save-wizard-draft"
                    disabled={createCampaign.isPending}
                    className="bg-amber-600 text-white gap-2"
                    onClick={() => {
                      createCampaign.mutate({
                        name: metaCampaignName,
                        platform: "Meta",
                        budget: parseFloat(metaDailyBudget) * 30 || 0,
                        dailyBudget: parseFloat(metaDailyBudget) || 0,
                        status: "DRAFT",
                        objective: metaObjective,
                        targetAudience: metaTargetAudience || null,
                        adCopy: JSON.stringify({ headline: metaHeadline || metaCampaignName, bodyText: metaMessage || "", cta: metaCta || "LEARN_MORE" }),
                        campaignMetadata: {
                          objective: metaObjective,
                          linkUrl: metaLinkUrl,
                          headline: metaHeadline || metaCampaignName,
                          bodyText: metaMessage || "",
                          ageMin: parseInt(metaAgeMin),
                          ageMax: parseInt(metaAgeMax),
                          genders: metaGender === "all" ? "all" : metaGender,
                          countries: metaGeoMode === "country" ? selectedCountries : undefined,
                          cities: metaGeoMode === "local" ? metaCities.split(",").map((c: string) => c.trim()).filter(Boolean) : undefined,
                          placements: metaPlacements.length > 0 ? metaPlacements : undefined,
                          callToAction: metaCta,
                          pixelId: metaPixelId || undefined,
                          customAudienceId: metaCustomAudienceId || undefined,
                          specialAdCategory: metaSpecialAdCategory !== "NONE" ? metaSpecialAdCategory : undefined,
                          advantageAudience: metaAdvantageAudience || undefined,
                          advantageShopping: metaAdvantageShopping || undefined,
                          automaticPlacements: metaAutomaticPlacements || undefined,
                          displayUrl: metaDisplayUrl || undefined,
                        },
                      }, {
                        onSuccess: () => {
                          toast({ title: "Draft Saved", description: "Campaign saved as draft. Connect Meta in Settings to launch it." });
                          setShowMetaForm(false);
                          setMetaFormStep(0);
                        },
                        onError: (err: any) => toast({ title: "Save Failed", description: err.message, variant: "destructive" }),
                      });
                    }}
                  >
                    {createCampaign.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save Draft
                  </Button>
                ) : (
                  <Button
                    data-testid="button-create-meta-campaign"
                    onClick={async () => {
                      if (editingCampaignId) {
                        const existingCampaign = campaigns?.find((c: any) => c.id === editingCampaignId);
                        const updPayload: any = {
                          id: editingCampaignId,
                          name: metaCampaignName,
                          budget: parseFloat(metaDailyBudget) || 0,
                          dailyBudget: parseFloat(metaDailyBudget) || 0,
                          targetAudience: metaTargetAudience || undefined,
                          adCopy: metaMessage ? JSON.stringify({ bodyText: metaMessage, headline: metaHeadline || metaCampaignName, cta: metaCta || "LEARN_MORE" }) : undefined,
                          creativeUrl: metaImageUrl || undefined,
                          startDate: metaStartDate || undefined,
                          endDate: metaEndDate || undefined,
                          campaignMetadata: {
                            ...((existingCampaign as any)?.campaignMetadata || {}),
                            objective: metaObjective,
                            linkUrl: metaLinkUrl,
                            ageMin: parseInt(metaAgeMin),
                            ageMax: parseInt(metaAgeMax),
                            genders: gendersPayload,
                            countries: metaGeoMode === "country" ? selectedCountries : undefined,
                            cities: metaGeoMode === "local" ? metaCities.split(",").map(c => c.trim()).filter(Boolean) : undefined,
                            provinces: metaProvinces ? metaProvinces.split(",").map(p => p.trim()).filter(Boolean) : undefined,
                            zips: metaZips ? metaZips.split(",").map(z => z.trim()).filter(Boolean) : undefined,
                            behaviors: metaBehaviors.length > 0 ? metaBehaviors : undefined,
                            placements: metaPlacements.length > 0 ? metaPlacements : undefined,
                            callToAction: metaCta,
                            pixelId: metaPixelId || undefined,
                            customAudienceId: metaCustomAudienceId || undefined,
                            specialAdCategory: metaSpecialAdCategory !== "NONE" ? metaSpecialAdCategory : undefined,
                            advantageAudience: metaAdvantageAudience || undefined,
                            advantageShopping: metaAdvantageShopping || undefined,
                            automaticPlacements: metaAutomaticPlacements || undefined,
                            displayUrl: metaDisplayUrl || undefined,
                          },
                        };
                        updateCampaign.mutate(updPayload, {
                          onSuccess: async () => {
                            if ((existingCampaign as any)?.metaCampaignId) {
                              try { await apiRequest("PATCH", `/api/campaigns/${editingCampaignId}/meta/fields`, { name: metaCampaignName, dailyBudgetCents: Math.round(parseFloat(metaDailyBudget) * 100) }); } catch {}
                              if ((existingCampaign as any)?.metaAdSetId) {
                                try { await apiRequest("PATCH", `/api/campaigns/${editingCampaignId}/meta/adset`, { ageMin: parseInt(metaAgeMin), ageMax: parseInt(metaAgeMax), genders: gendersPayload, countries: metaGeoMode === "country" ? selectedCountries : undefined, placements: metaPlacements.length > 0 ? metaPlacements : undefined }); } catch {}
                              }
                              toast({ title: "Campaign Updated", description: "Saved locally and synced to Meta." });
                            } else {
                              toast({ title: "Campaign Updated", description: "Campaign details saved." });
                            }
                            setShowMetaForm(false);
                            setEditingCampaignId(null);
                          },
                          onError: (err: any) => toast({ title: "Update Failed", description: err.message, variant: "destructive" }),
                        });
                        return;
                      }
                      if (leadFormEnabled && !leadFormPrivacyUrl.trim()) {
                        setLeadFormPrivacyError("Privacy Policy URL is required for lead forms. Add https://yoursite.com/privacy");
                        return;
                      }
                      if (leadFormEnabled && leadFormPrivacyUrl.trim() && !leadFormPrivacyUrl.trim().startsWith("http")) {
                        setLeadFormPrivacyError("Privacy Policy URL must start with https://");
                        return;
                      }
                      let createdLeadFormId = leadFormId;
                      if (leadFormEnabled && !createdLeadFormId && leadFormPrivacyUrl && leadFormQuestions.length > 0) {
                        setLeadFormCreating(true);
                        try {
                          const res = await fetch(`/api/workspaces/${activeWorkspaceId}/meta/leadgen-forms`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({
                              name: `${metaCampaignName} - Lead Form`,
                              privacyPolicyUrl: leadFormPrivacyUrl,
                              questions: leadFormQuestions,
                              thankYouUrl: leadFormThankYouUrl || undefined,
                            }),
                          });
                          if (res.ok) {
                            const data = await res.json();
                            createdLeadFormId = data.id;
                            setLeadFormId(data.id);
                            setLeadFormCapabilityError(false);
                          } else {
                            const errData = await res.json().catch(() => ({}));
                            setLeadFormCreating(false);
                            if (errData.errorCode === "LEAD_ADS_NOT_ENABLED") {
                              setLeadFormCapabilityError(true);
                              toast({
                                title: "Lead Ads not enabled on your Facebook app",
                                description: "Enable Lead Generation in Meta Business Manager, or click 'Proceed without Lead Form' to launch as a Traffic campaign.",
                                variant: "destructive",
                              });
                            } else {
                              toast({ title: "Lead Form Creation Failed", description: errData.message || "Could not create the lead gen form on Meta.", variant: "destructive" });
                            }
                            return;
                          }
                        } catch (err: any) {
                          toast({ title: "Lead Form Error", description: err.message || "Network error creating lead form.", variant: "destructive" });
                          setLeadFormCreating(false);
                          return;
                        }
                        setLeadFormCreating(false);
                      }
                      const wizardBlockers: string[] = [];
                      if (!metaHeadline.trim()) wizardBlockers.push("Headline");
                      if (!metaMessage.trim()) wizardBlockers.push("Ad body text");
                      if (metaCreativeMode === "none" && !metaImageUrl && !metaVideoUrl) wizardBlockers.push("Creative (image or video)");
                      if (wizardBlockers.length > 0) {
                        toast({ title: "Missing required fields", description: `Please fill in: ${wizardBlockers.join(", ")}`, variant: "destructive" });
                        return;
                      }
                      createMetaCampaign.mutate({
                        adAccountId: metaAdAccountId,
                        name: metaCampaignName,
                        objective: META_OBJECTIVE_MAP[metaObjective] || metaObjective,
                        dailyBudget: metaDailyBudget,
                        message: metaMessage,
                        headline: metaHeadline || undefined,
                        description: metaDescription || undefined,
                        displayUrl: metaDisplayUrl || undefined,
                        callToAction: metaCta || "LEARN_MORE",
                        pixelId: metaPixelId || undefined,
                        customAudienceId: metaCustomAudienceId || undefined,
                        specialAdCategory: metaSpecialAdCategory !== "NONE" ? metaSpecialAdCategory : undefined,
                        advantageAudience: metaAdvantageAudience || undefined,
                        advantageShopping: metaAdvantageShopping || undefined,
                        automaticPlacements: metaAutomaticPlacements || undefined,
                        targetAudience: metaTargetAudience || undefined,
                        countries: metaGeoMode === "country" ? selectedCountries : undefined,
                        cities: metaGeoMode === "local" ? metaCities.split(",").map(c => c.trim()).filter(Boolean) : undefined,
                        provinces: metaProvinces ? metaProvinces.split(",").map(p => p.trim()).filter(Boolean) : undefined,
                        zips: metaZips ? metaZips.split(",").map(z => z.trim()).filter(Boolean) : undefined,
                        behaviors: metaBehaviors.length > 0 ? metaBehaviors : undefined,
                        ageMin: metaAgeMin,
                        ageMax: metaAgeMax,
                        genders: gendersPayload,
                        placements: metaAutomaticPlacements ? undefined : (metaPlacements.length > 0 ? metaPlacements : undefined),
                        imageData: metaCreativeMode !== "video_url" ? (metaImageData || undefined) : undefined,
                        videoData: metaCreativeMode === "video_url" ? (metaVideoUrl || undefined) : undefined,
                        linkUrl: metaLinkUrl || undefined,
                        startDate: metaStartDate || undefined,
                        endDate: metaEndDate || undefined,
                        status: metaStatus,
                        leadFormId: createdLeadFormId || undefined,
                      });
                    }}
                    disabled={createMetaCampaign.isPending || updateCampaign.isPending || leadFormCreating}
                    className="bg-emerald-600 text-white gap-2"
                  >
                    {(createMetaCampaign.isPending || updateCampaign.isPending || leadFormCreating) ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                    {editingCampaignId ? "Save Campaign" : "Launch Campaign"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })()}


      {showCreativeGen && (
        <Card className="bg-card/50 border-border max-w-2xl" data-testid="creative-gen-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display text-foreground flex items-center gap-2">
              <Sparkles size={16} className="text-purple-400" /> AI Ad Creative Generator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Platform</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => (
                    <ToggleChip
                      key={p}
                      label={p}
                      active={creativePlatform === p}
                      onClick={() => setCreativePlatform(p)}
                      testId={`button-creative-platform-${p.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tone</label>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((t) => (
                    <ToggleChip
                      key={t}
                      label={t}
                      active={creativeTone === t}
                      onClick={() => setCreativeTone(t)}
                      testId={`button-creative-tone-${t.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <Input
              data-testid="input-creative-objective"
              value={creativeObjective}
              onChange={(e) => setCreativeObjective(e.target.value)}
              placeholder="Campaign objective (e.g., Increase brand awareness)"
              className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/60"
            />
            <Input
              data-testid="input-creative-audience"
              value={creativeAudience}
              onChange={(e) => setCreativeAudience(e.target.value)}
              placeholder="Target audience (e.g., 25-35 year old professionals)"
              className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/60"
            />
            <Input
              data-testid="input-creative-product"
              value={creativeProduct}
              onChange={(e) => setCreativeProduct(e.target.value)}
              placeholder="Product/Service (e.g., SaaS project management tool)"
              className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/60"
            />
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="button-generate-ad-creative"
                onClick={handleGenerateCreative}
                disabled={generateCreative.isPending || !creativeObjective.trim() || !creativeAudience.trim() || !creativeProduct.trim()}
                className="gap-2"
              >
                {generateCreative.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Generate
              </Button>
              <Button
                data-testid="button-cancel-creative"
                variant="ghost"
                onClick={() => { setShowCreativeGen(false); setCreativeResult(null); }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {creativeResult && (
        <Card className="bg-gradient-to-br from-purple-950/20 to-transparent border-purple-500/15" data-testid="creative-result-panel">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-base font-display text-foreground flex items-center gap-2">
              <Sparkles size={16} className="text-purple-400" /> Generated Ad Creative
            </CardTitle>
            <Button data-testid="button-close-creative-result" size="icon" variant="ghost" onClick={() => setCreativeResult(null)}>
              <X size={16} />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            {creativeResult.adImageUrl && (
              <div className="rounded-lg overflow-hidden border border-border">
                <img src={creativeResult.adImageUrl} alt="Generated ad" className="w-full h-auto" />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Headline</label>
                <div className="bg-muted/50 border border-border rounded-lg px-4 py-3 text-sm text-foreground">{creativeResult.headline}</div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Call to Action</label>
                <div className="bg-muted/50 border border-border rounded-lg px-4 py-3 text-sm text-foreground">{creativeResult.cta}</div>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Body Copy</label>
              <div className="bg-muted/50 border border-border rounded-lg px-4 py-3 text-sm text-foreground">{creativeResult.body}</div>
            </div>
            {creativeResult.variations && creativeResult.variations.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Variations</label>
                <div className="space-y-2">
                  {creativeResult.variations.map((v, i) => (
                    <div key={i} className="bg-muted/50 border border-border rounded-lg px-4 py-3 text-sm text-foreground">{v}</div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Targeting Notes</label>
                <div className="bg-muted/50 border border-border rounded-lg px-4 py-3 text-sm text-foreground/70">{creativeResult.targetingNotes}</div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Image Prompt</label>
                <div className="bg-muted/50 border border-border rounded-lg px-4 py-3 text-sm text-foreground/70">{creativeResult.imagePrompt}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-row">
          <StatCard icon={DollarSign} label="Total Budget" value={`$${stats.totalBudget.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
          <StatCard icon={BarChart3} label="Total Spent" value={`$${stats.totalSpent.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} subtitle={stats.totalBudget > 0 ? `${((stats.totalSpent / stats.totalBudget) * 100).toFixed(1)}% of budget` : undefined} />
          <StatCard icon={TrendingUp} label="Conversions" value={stats.totalConversions.toLocaleString()} />
          <StatCard icon={Target} label="Active" value={String(stats.activeCampaigns)} subtitle={`of ${campaigns?.length || 0} campaigns`} />
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-1 p-1 rounded-lg bg-card/50 border border-border">
            <button
              data-testid="tab-campaigns"
              onClick={() => setActiveTab("campaigns")}
              className={`px-4 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-2 ${
                activeTab === "campaigns" ? "bg-blue-600/20 text-blue-400" : "text-muted-foreground hover-elevate"
              }`}
            >
              <LayoutDashboard size={14} /> Campaigns
            </button>
            <button
              data-testid="tab-analytics"
              onClick={() => setActiveTab("analytics")}
              className={`px-4 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-2 ${
                activeTab === "analytics" ? "bg-blue-600/20 text-blue-400" : "text-muted-foreground hover-elevate"
              }`}
            >
              <BarChart3 size={14} /> Analytics
            </button>
            <button
              data-testid="tab-creative"
              onClick={() => setActiveTab("creative")}
              className={`px-4 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-2 ${
                activeTab === "creative" ? "bg-purple-600/20 text-purple-400" : "text-muted-foreground hover-elevate"
              }`}
            >
              <Sparkles size={14} /> Creative Studio
            </button>
          </div>

          {activeTab === "campaigns" && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted-foreground/70 font-semibold mr-1">Filter:</span>
              <ToggleChip label="All" active={!filters.platform} onClick={() => setFilters({ ...filters, platform: null })} testId="filter-platform-all" />
              {PLATFORMS.map((p) => (
                <ToggleChip key={p} label={p} active={filters.platform === p} onClick={() => setFilters({ ...filters, platform: p })} testId={`filter-platform-${p.toLowerCase()}`} />
              ))}
              <span className="w-px h-5 bg-muted mx-1" />
              <ToggleChip label="All" active={!filters.status} onClick={() => setFilters({ ...filters, status: null })} testId="filter-status-all" />
              {STATUSES.map((s) => (
                <ToggleChip key={s} label={s} active={filters.status === s} onClick={() => setFilters({ ...filters, status: s })} testId={`filter-status-${s.toLowerCase()}`} />
              ))}
            </div>
          )}
        </div>

      {activeTab === "campaigns" && (
        <>
          {peteRunning && (
            <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="pete-running-banner">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-full bg-amber-500/20 animate-pulse">
                  <Bot size={16} className="text-amber-400" />
                </div>
                <p className="text-xs font-medium text-amber-400/90">
                  Pete is building your campaigns — usually 1–3 min.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="/strategy"
                  className="text-[10px] font-semibold text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors"
                  data-testid="link-watch-pete-stream"
                >
                  Watch Pete work →
                </a>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-[10px] gap-1.5 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "meta", "campaigns"] });
                    queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${activeWorkspaceId}/campaigns`] });
                    toast({ title: "Refreshing...", description: "Checking for new campaigns from Pete." });
                  }}
                  data-testid="button-refresh-pete"
                >
                  <Zap size={10} /> Refresh Now
                </Button>
                <button 
                  onClick={() => setPeteRunning(false)} 
                  className="text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                  data-testid="button-dismiss-pete-banner"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(3).fill(0).map((_, i) => (
                <Card key={i} className="bg-card/50 border-border">
                  <CardContent className="p-5">
                    <div className="h-40 bg-card/50 animate-pulse rounded-lg" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredCampaigns.length > 0 ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="campaigns-grid">
              {filteredCampaigns.map((campaign: AdCampaign) => {
                const spentRatio = campaign.budget > 0 ? (campaign.spent / campaign.budget) * 100 : 0;
                const isMetaLive = !!(campaign as any).metaCampaignId;
                const isPeteDraft = !isMetaLive && campaign.status === "draft";
                const showingInsights = insightsCampaignId === campaign.id;
                const isLaunching = launchingCampaignId === campaign.id;

                return (
                  <Card
                    key={campaign.id}
                    className={`bg-muted/30 border-border transition-all border-l-4 ${
                      isMetaLive ? "border-l-emerald-500/60 border-emerald-500/15" :
                      isPeteDraft ? "border-l-amber-500/60 border-amber-500/15" :
                      "border-l-transparent"
                    }`}
                    data-testid={`campaign-card-${campaign.id}`}
                  >
                    <CardContent className="p-5 space-y-4">
                      {(campaign as any).creativeUrl && (
                        <div className="rounded-lg overflow-hidden border border-border" data-testid={`campaign-creative-${campaign.id}`}>
                          <img src={(campaign as any).creativeUrl} alt="Ad creative" className="w-full max-h-24 object-cover" />
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-2 min-w-0 flex-1">
                          <h3 className="text-sm font-bold text-foreground truncate">{campaign.name}</h3>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <PlatformBadge platform={campaign.platform} />
                            {isMetaLive && (
                              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[10px] gap-1">
                                <Radio size={8} /> LIVE ON META
                              </Badge>
                            )}
                            {campaign.status === "FAILED" && (
                              <Badge className="bg-red-500/15 text-red-400 border-red-500/20 text-[10px] gap-1">
                                <AlertCircle size={8} /> LAUNCH FAILED
                              </Badge>
                            )}
                            {isPeteDraft && (
                              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[10px] gap-1" title="Pete created this draft — review and launch when ready">
                                <Bot size={8} /> Pete Draft
                              </Badge>
                            )}
                            {isMetaLive && (campaign as any).metaCampaignId && (
                              <a
                                href={buildAdsManagerUrl((campaign as any).metaAdAccountId || metaAdAccountId || "", (campaign as any).metaCampaignId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[9px] font-mono text-blue-400/70 hover:text-blue-400 underline select-all"
                                data-testid={`meta-campaign-link-${campaign.id}`}
                                title="Open in Meta Ads Manager"
                              >
                                ID: {(campaign as any).metaCampaignId} ↗
                              </a>
                            )}
                          </div>
                          {(() => {
                            let adCopyParsed: { headline?: string; bodyText?: string; cta?: string } | null = null;
                            try { adCopyParsed = (campaign as any).adCopy ? JSON.parse((campaign as any).adCopy) : null; } catch {}
                            const meta = (campaign as any).campaignMetadata || {};
                            const geoList: string[] = meta.geoLocations || [];
                            const interestList: string[] = meta.interests || [];
                            const dailyBudgetDisplay = (campaign as any).dailyBudget || (campaign.budget ? (campaign.budget / 30).toFixed(0) : null);
                            const createdDate = (campaign as any).createdAt ? new Date((campaign as any).createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
                            return (
                              <>
                                <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground/60">
                                  {dailyBudgetDisplay && <span data-testid={`campaign-daily-budget-${campaign.id}`}>${dailyBudgetDisplay}/day</span>}
                                  {createdDate && <span data-testid={`campaign-created-${campaign.id}`}>{createdDate}</span>}
                                </div>
                                {adCopyParsed?.headline && (
                                  <p className="text-xs font-semibold text-foreground/80 mt-1" data-testid={`campaign-headline-${campaign.id}`}>{adCopyParsed.headline}</p>
                                )}
                                {adCopyParsed?.bodyText && (
                                  <p className="text-[11px] text-muted-foreground/70 leading-snug line-clamp-2">{adCopyParsed.bodyText}</p>
                                )}
                                {(geoList.length > 0 || interestList.length > 0) && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {geoList.slice(0, 2).map((g: string) => (
                                      <span key={g} className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded-full">{g}</span>
                                    ))}
                                    {interestList.slice(0, 2).map((i: string) => (
                                      <span key={i} className="text-[9px] bg-purple-500/10 text-purple-400 border border-purple-500/20 px-1.5 py-0.5 rounded-full">{i}</span>
                                    ))}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <StatusBadge status={campaign.status} />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold flex items-center gap-1">
                            <DollarSign size={10} /> Budget
                          </span>
                          <span className="text-sm font-bold text-foreground" data-testid={`campaign-budget-${campaign.id}`}>
                            ${campaign.budget.toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold flex items-center gap-1">
                            <BarChart3 size={10} /> Spent
                          </span>
                          <span className="text-sm font-bold text-foreground" data-testid={`campaign-spent-${campaign.id}`}>
                            ${campaign.spent.toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold flex items-center gap-1">
                            <TrendingUp size={10} /> Conv.
                          </span>
                          <span className="text-sm font-bold text-foreground" data-testid={`campaign-conversions-${campaign.id}`}>
                            {campaign.conversions}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground/70 font-semibold">Budget Used</span>
                          <span className="text-[10px] font-bold text-muted-foreground" data-testid={`campaign-ratio-${campaign.id}`}>{spentRatio.toFixed(1)}%</span>
                        </div>
                        <Progress value={Math.min(spentRatio, 100)} className="h-1.5 bg-muted" data-testid={`campaign-progress-${campaign.id}`} />
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap pt-1">
                        {isMetaLive ? (
                          <>
                            <Button
                              data-testid={`button-meta-toggle-${campaign.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => toggleMetaStatus.mutate({
                                id: campaign.id,
                                status: campaign.status === "ACTIVE" ? "PAUSED" : "ACTIVE"
                              })}
                              disabled={toggleMetaStatus.isPending}
                              className={`text-xs gap-1.5 ${
                                campaign.status === "ACTIVE"
                                  ? "border-amber-500/20 text-amber-400"
                                  : "border-emerald-500/20 text-emerald-400"
                              }`}
                            >
                              {campaign.status === "ACTIVE" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Activate</>}
                            </Button>
                            <Button
                              data-testid={`button-meta-insights-${campaign.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setInsightsCampaignId(showingInsights ? null : campaign.id)}
                              className="text-xs gap-1.5 border-blue-500/20 text-blue-400"
                            >
                              <Activity size={12} /> {showingInsights ? "Hide" : "Insights"}
                            </Button>
                            {(campaign as any).metaCampaignId && (
                              <a
                                href={buildAdsManagerUrl((campaign as any).metaAdAccountId || metaAdAccountId || "", (campaign as any).metaCampaignId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`button-view-meta-manager-${campaign.id}`}
                              >
                                <Button size="sm" variant="outline" className="text-xs gap-1.5 border-border text-muted-foreground">
                                  <ExternalLink size={12} /> Meta Manager
                                </Button>
                              </a>
                            )}
                          </>
                        ) : (
                          <>
                            {isPeteDraft && (
                              <Button
                                data-testid={`button-approve-launch-${campaign.id}`}
                                size="sm"
                                onClick={() => { setReviewCampaign(campaign); setLaunchAsPaused(false); }}
                                disabled={isLaunching}
                                className="text-xs gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500"
                              >
                                {isLaunching ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />}
                                {isLaunching ? "Launching..." : "Approve & Launch"}
                              </Button>
                            )}
                            {STATUSES.map((s) => (
                              <button
                                key={s}
                                data-testid={`button-status-${campaign.id}-${s.toLowerCase()}`}
                                onClick={() => handleStatusChange(campaign, s)}
                                disabled={campaign.status === s || updateCampaign.isPending}
                                className={`text-[10px] font-bold px-2.5 py-1.5 rounded-md transition-all ${
                                  campaign.status === s
                                    ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                                    : "bg-muted/50 text-muted-foreground/70 border border-border hover-elevate"
                                }`}
                              >
                                {s}
                              </button>
                            ))}
                          </>
                        )}
                        <Button
                          data-testid={`button-edit-campaign-${campaign.id}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => openCampaignBuilder(campaign)}
                          className="ml-auto text-muted-foreground/50"
                        >
                          <Settings2 size={14} />
                        </Button>
                        <Button
                          data-testid={`button-delete-campaign-${campaign.id}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            if (isMetaLive) {
                              setDeleteConfirmCampaign(campaign);
                            } else {
                              deleteCampaign.mutate(campaign.id, { onError: (err: any) => toast({ title: "Delete Failed", description: err.message, variant: "destructive" }) });
                            }
                          }}
                          disabled={deleteCampaign.isPending}
                          className="text-muted-foreground/50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>

                      {showingInsights && (
                        <div className="border-t border-border pt-4 space-y-3">
                          {insightsLoading ? (
                            <div className="grid grid-cols-2 gap-3 py-2">
                              {Array(4).fill(0).map((_, sk) => (
                                <div key={sk} className="space-y-1.5">
                                  <div className="h-2.5 w-16 bg-muted animate-pulse rounded" />
                                  <div className="h-4 w-12 bg-muted animate-pulse rounded" />
                                </div>
                              ))}
                            </div>
                          ) : campaignInsights && campaignInsights.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3">
                              {campaignInsights.map((insight: any, idx: number) => (
                                <div key={idx} className="space-y-3">
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold flex items-center gap-1">
                                      <Eye size={10} /> Impressions
                                    </span>
                                    <span className="text-sm font-bold text-foreground">{Number(insight.impressions || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold flex items-center gap-1">
                                      <MousePointerClick size={10} /> Clicks
                                    </span>
                                    <span className="text-sm font-bold text-foreground">{Number(insight.clicks || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Spend</span>
                                    <span className="text-sm font-bold text-foreground">${Number(insight.spend || 0).toFixed(2)}</span>
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">Reach</span>
                                    <span className="text-sm font-bold text-foreground">{Number(insight.reach || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">CTR</span>
                                    <span className="text-sm font-bold text-blue-400">{Number(insight.ctr || 0).toFixed(2)}%</span>
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-muted-foreground/70 uppercase font-semibold">CPC</span>
                                    <span className="text-sm font-bold text-foreground">${Number(insight.cpc || 0).toFixed(2)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground/70">No insights data available yet. Campaign needs time to gather data.</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : campaigns && campaigns.length > 0 ? (
            <Card className="bg-muted/30 border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Target size={32} className="text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">No campaigns match your filters.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-muted/30 border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="p-4 rounded-2xl bg-card/50 border border-border mb-4">
                  <Megaphone size={32} className="text-muted-foreground/40" />
                </div>
                <h3 className="text-base font-display font-bold text-muted-foreground mb-1">No Campaigns Yet</h3>
                <p className="text-sm text-muted-foreground/70 mb-4 max-w-sm text-center">Pete creates your campaigns from your marketing strategy. Click "Ask Pete" above to draft them, or import existing campaigns from Meta.</p>
                <div className="flex gap-2 flex-wrap justify-center">
                          <Button
                            variant="outline"
                            className="gap-2 border-amber-500/30 text-amber-400 no-default-active-elevate"
                            data-testid="button-empty-ask-pete"
                            onClick={() => { setAskPeteBrief(""); setShowAskPeteDialog(true); }}
                            disabled={peteRunning}
                          >
                            {peteRunning ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />} Ask Pete
                          </Button>
                  <Button onClick={() => setShowImportMeta(true)} variant="outline" className="gap-2" data-testid="button-empty-import-meta">
                    <ArrowLeft size={14} /> Import from Meta
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activeTab === "analytics" && campaigns && campaigns.length > 0 && (() => {
        const CONVERSION_VALUE = 50;
        const maxBudget = Math.max(...campaigns.map(c => c.budget), 1);

        const platformMap = campaigns.reduce<Record<string, { budget: number; spent: number; conversions: number; count: number }>>((acc, c) => {
          if (!acc[c.platform]) acc[c.platform] = { budget: 0, spent: 0, conversions: 0, count: 0 };
          acc[c.platform].budget += c.budget;
          acc[c.platform].spent += c.spent;
          acc[c.platform].conversions += c.conversions;
          acc[c.platform].count += 1;
          return acc;
        }, {});

        return (
          <div className="space-y-4" data-testid="analytics-section">
            <Card className="bg-muted/30 border-border" data-testid="analytics-budget-spend">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-display text-foreground flex items-center gap-2">
                  <DollarSign size={14} className="text-blue-400" /> Budget vs Spend
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                {campaigns.map((c) => {
                  const budgetPct = (c.budget / maxBudget) * 100;
                  const spentPct = c.budget > 0 ? (c.spent / maxBudget) * 100 : 0;
                  return (
                    <div key={c.id} className="space-y-1.5" data-testid={`analytics-bar-${c.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-foreground font-medium truncate">{c.name}</span>
                        <span className="text-[10px] text-muted-foreground/70 shrink-0 font-mono">${c.budget.toFixed(0)} / ${c.spent.toFixed(0)}</span>
                      </div>
                      <div className="relative h-4 w-full bg-muted/50 rounded-md overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-blue-600/25 rounded-md" style={{ width: `${budgetPct}%` }} />
                        <div className="absolute inset-y-0 left-0 bg-blue-500 rounded-md" style={{ width: `${spentPct}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center gap-4 text-[10px] text-muted-foreground/70 pt-1">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-blue-600/25" /><span>Budget</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-blue-500" /><span>Spent</span></div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-muted/30 border-border" data-testid="analytics-roi">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-display text-foreground flex items-center gap-2">
                    <TrendingUp size={14} className="text-emerald-400" /> Campaign ROI
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 pt-2">
                  {campaigns.map((c) => {
                    const revenue = c.conversions * CONVERSION_VALUE;
                    const roi = c.spent > 0 ? ((revenue - c.spent) / c.spent) * 100 : 0;
                    return (
                      <div key={c.id} className="flex items-center justify-between gap-2 py-2.5 border-b border-border last:border-b-0" data-testid={`roi-row-${c.id}`}>
                        <div className="min-w-0">
                          <span className="text-xs text-foreground font-medium truncate block">{c.name}</span>
                          <span className="text-[10px] text-muted-foreground/60">{c.conversions} conv. x ${CONVERSION_VALUE} = ${revenue}</span>
                        </div>
                        <span className={`text-xs font-bold shrink-0 font-mono ${roi >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`roi-value-${c.id}`}>
                          {roi >= 0 ? "+" : ""}{roi.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="bg-muted/30 border-border" data-testid="analytics-efficiency">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-display text-foreground flex items-center gap-2">
                    <Activity size={14} className="text-amber-400" /> Spend Efficiency
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-2">
                  {campaigns.map((c) => {
                    const eff = c.budget > 0 ? (c.spent / c.budget) * 100 : 0;
                    const color = eff > 90 ? "text-red-400" : eff >= 70 ? "text-amber-400" : "text-emerald-400";
                    return (
                      <div key={c.id} className="space-y-1.5" data-testid={`efficiency-row-${c.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-foreground font-medium truncate">{c.name}</span>
                          <span className={`text-xs font-bold font-mono shrink-0 ${color}`} data-testid={`efficiency-value-${c.id}`}>{eff.toFixed(1)}%</span>
                        </div>
                        <Progress value={Math.min(eff, 100)} className="h-1.5 bg-muted/50" data-testid={`efficiency-bar-${c.id}`} />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            <Card className="bg-muted/30 border-border" data-testid="analytics-platform-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-display text-foreground flex items-center gap-2">
                  <Globe size={14} className="text-sky-400" /> Platform Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(platformMap).map(([plat, data]) => {
                    const pEff = data.budget > 0 ? (data.spent / data.budget) * 100 : 0;
                    const pRoi = data.spent > 0 ? ((data.conversions * CONVERSION_VALUE - data.spent) / data.spent) * 100 : 0;
                    return (
                      <div key={plat} className="bg-card/50 border border-border rounded-lg p-4 space-y-3" data-testid={`platform-card-${plat.toLowerCase()}`}>
                        <div className="flex items-center justify-between gap-2">
                          <PlatformBadge platform={plat} />
                          <span className="text-[10px] text-muted-foreground/70 font-mono">{data.count} camp.</span>
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground/70">Budget</span>
                            <span className="text-foreground font-medium font-mono" data-testid={`platform-budget-${plat.toLowerCase()}`}>${data.budget.toFixed(0)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground/70">Spent</span>
                            <span className="text-foreground font-medium font-mono" data-testid={`platform-spent-${plat.toLowerCase()}`}>${data.spent.toFixed(0)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground/70">Conv.</span>
                            <span className="text-foreground font-medium font-mono" data-testid={`platform-conversions-${plat.toLowerCase()}`}>{data.conversions}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
                            <span className="text-muted-foreground/70">ROI</span>
                            <span className={`font-bold font-mono ${pRoi >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`platform-roi-${plat.toLowerCase()}`}>
                              {pRoi >= 0 ? "+" : ""}{pRoi.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

    {/* T005: Pete's Spend This Week — shown in analytics tab */}
    {activeTab === "analytics" && (
      <Card className="bg-gradient-to-br from-emerald-950/20 to-transparent border-emerald-500/15 mt-4" data-testid="budget-envelope-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm font-display text-foreground flex items-center gap-2">
              <DollarSign size={14} className="text-emerald-400" /> Pete's Spend This Week
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
              disabled={budgetCheckRunning}
              onClick={runBudgetCheck}
              data-testid="button-run-budget-check"
            >
              {budgetCheckRunning ? <Loader2 size={11} className="animate-spin mr-1" /> : <Bot size={11} className="mr-1" />}
              {budgetCheckRunning ? "Checking…" : "Run Pete Check"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-0.5">Pete launches and pauses campaigns within your weekly envelope. Resets every Monday.</p>
        </CardHeader>
        <CardContent className="pt-2 space-y-5">
          {/* Per-platform spend rows */}
          <div className="space-y-4">
            {(["meta", "google", "linkedin", "x"] as const).map((platform) => {
              const env = budgetEnvelopes.find((e: any) => e.platform === platform);
              const spent = env?.currentWeekSpentCents ?? 0;
              const limit = env?.weeklyLimitCents ?? 0;
              const remaining = Math.max(limit - spent, 0);
              const pct = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
              const barColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
              const platformLabel = { meta: "Meta", google: "Google", linkedin: "LinkedIn", x: "X / Twitter" }[platform];
              return (
                <div key={platform} className="space-y-2" data-testid={`envelope-row-${platform}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-foreground">{platformLabel}</span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground/70">
                        <span data-testid={`envelope-spent-${platform}`}>${(spent / 100).toFixed(2)} used</span>
                        <span className="text-muted-foreground/30">·</span>
                        <span data-testid={`envelope-remaining-${platform}`}>{limit > 0 ? `$${(remaining / 100).toFixed(2)} left` : "No limit"}</span>
                      </div>
                      <button
                        className="text-[10px] text-blue-400 hover:text-blue-300 underline"
                        data-testid={`button-set-envelope-${platform}`}
                        onClick={() => {
                          setEnvelopeModal({ platform, label: platformLabel, currentCents: limit });
                          setEnvelopeInput(limit > 0 ? (limit / 100).toFixed(2) : "");
                        }}
                      >
                        {limit > 0 ? "Edit" : "Set limit"}
                      </button>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${barColor}`}
                      style={{ width: limit > 0 ? `${pct}%` : "0%" }}
                      data-testid={`envelope-bar-${platform}`}
                    />
                  </div>
                  {limit > 0 && (
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground/50">
                      <span>${(spent / 100).toFixed(2)} spent</span>
                      <span>{pct.toFixed(0)}% of ${(limit / 100).toFixed(2)}/wk</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mini action timeline */}
          {peteLogs.length > 0 && (
            <div className="border-t border-border/50 pt-4" data-testid="pete-spend-timeline">
              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-bold mb-3">Recent Pete Actions</p>
              <div className="space-y-2">
                {peteLogs.slice(0, 5).map((log: any) => {
                  const isLaunch = log.action === "CAMPAIGN_LAUNCHED";
                  const isExhausted = log.action === "BUDGET_EXHAUSTED";
                  const isBlocked = log.action === "BUDGET_BLOCKED_LAUNCH";
                  const iconColor = isLaunch ? "text-emerald-400" : isExhausted || isBlocked ? "text-red-400" : "text-amber-400";
                  const Icon = isLaunch ? Rocket : isExhausted || isBlocked ? AlertCircle : Activity;
                  return (
                    <div key={log.id} className="flex items-start gap-2.5" data-testid={`pete-log-${log.id}`}>
                      <Icon size={11} className={`${iconColor} mt-0.5 shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-foreground/80 leading-snug line-clamp-2">{log.details}</p>
                        <p className="text-[10px] text-muted-foreground/40 mt-0.5">
                          {log.createdAt ? new Date(log.createdAt).toLocaleDateString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )}

    {/* Envelope edit modal */}
    {envelopeModal && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        data-testid="envelope-edit-modal"
        onClick={(e) => { if (e.target === e.currentTarget) setEnvelopeModal(null); }}
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-foreground text-base">Set Weekly Budget</h3>
            <button onClick={() => setEnvelopeModal(null)} className="text-muted-foreground hover:text-foreground" data-testid="button-close-envelope-modal"><X size={16} /></button>
          </div>
          <p className="text-sm text-muted-foreground">Pete will autonomously launch and pause <strong>{envelopeModal.label}</strong> campaigns within this weekly limit.</p>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block">Weekly Limit ($)</label>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">$</span>
              <input
                type="number"
                min="0"
                step="10"
                value={envelopeInput}
                onChange={(e) => setEnvelopeInput(e.target.value)}
                placeholder="e.g. 200"
                className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground"
                data-testid="input-envelope-limit"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const cents = Math.round(parseFloat(envelopeInput) * 100);
                    if (!isNaN(cents) && cents >= 0) {
                      upsertEnvelope.mutate({ platform: envelopeModal.platform, weeklyLimitCents: cents });
                      setEnvelopeModal(null);
                    }
                  }
                }}
              />
              <span className="text-[11px] text-muted-foreground/60">/week</span>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setEnvelopeModal(null)}
              data-testid="button-cancel-envelope"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={() => {
                const cents = Math.round(parseFloat(envelopeInput) * 100);
                if (!isNaN(cents) && cents >= 0) {
                  upsertEnvelope.mutate({ platform: envelopeModal.platform, weeklyLimitCents: cents });
                  setEnvelopeModal(null);
                }
              }}
              data-testid="button-save-envelope"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    )}
    </div>

    {/* Pre-launch Review Dialog */}
    {reviewCampaign && (() => {
      let headline = "";
      let bodyText = "";
      let ctaLabel = "Learn More";
      const reviewMeta = ((reviewCampaign as any).campaignMetadata || {}) as any;
      const rawCopy = (reviewCampaign as any).adCopy;
      if (rawCopy) {
        try {
          const parsed = typeof rawCopy === "string" ? JSON.parse(rawCopy) : rawCopy;
          if (parsed.headline) headline = parsed.headline;
          if (parsed.bodyText) bodyText = parsed.bodyText;
          if (parsed.cta) ctaLabel = parsed.cta;
        } catch {
          bodyText = String(rawCopy);
        }
      }
      if (!headline && reviewMeta.headline) headline = reviewMeta.headline;
      if (!bodyText && reviewMeta.bodyText) bodyText = reviewMeta.bodyText;
      if (!headline) headline = (reviewCampaign as any).name || "";
      const reviewGeo: string[] = reviewMeta.geoLocations || [];
      const reviewInterests: string[] = reviewMeta.interests || [];
      const reviewAgeMin = reviewMeta.ageMin || 25;
      const reviewAgeMax = reviewMeta.ageMax || 65;
      const creativeImg = (reviewCampaign as any).creativeUrl;
      const isLaunchingThis = launchingCampaignId === reviewCampaign.id;

      const launchBlockers: string[] = [];
      if (!headline || headline.trim().length === 0) launchBlockers.push("Headline is required");
      if (!bodyText || bodyText.trim().length === 0) launchBlockers.push("Ad body text is required");
      const hasVideo = !!(reviewCampaign as any).videoUrl || !!reviewMeta.videoUrl || !!reviewMeta.videoData;
      const hasImage = !!creativeImg || !!reviewMeta.imageData;
      if (!hasImage && !hasVideo) launchBlockers.push("Creative (image or video) is required");
      const hasMetaConnection = !!metaAdAccountId || !!(adAccounts && adAccounts.length > 0);
      if (!hasMetaConnection) launchBlockers.push("Meta Ads account must be connected");
      const canLaunch = launchBlockers.length === 0;

      return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="launch-review-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) { setReviewCampaign(null); } }}
        >
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-border flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Rocket size={18} className="text-emerald-400" />
                <h2 className="font-semibold text-foreground text-base">Review & Launch on Meta</h2>
              </div>
              <button
                onClick={() => setReviewCampaign(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-close-review-dialog"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Ad Preview */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Ad Preview</p>
                <div className="rounded-xl border border-border overflow-hidden bg-muted/20">
                  {creativeImg && (
                    <div className="aspect-video w-full overflow-hidden bg-black/20">
                      <img
                        src={creativeImg}
                        alt="Ad creative"
                        className="w-full h-full object-cover"
                        data-testid="review-ad-image"
                      />
                    </div>
                  )}
                  <div className="p-4 space-y-1.5">
                    <p className="font-bold text-foreground text-sm leading-tight" data-testid="review-ad-headline">{headline}</p>
                    {bodyText && (
                      <p className="text-xs text-muted-foreground line-clamp-3" data-testid="review-ad-body">{bodyText}</p>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-[11px] font-semibold text-blue-400 border border-blue-500/30 rounded px-2 py-0.5" data-testid="review-ad-cta">
                        {ctaLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Targeting */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Targeting</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-muted/20 rounded-lg p-3 space-y-0.5">
                    <p className="text-muted-foreground">Locations</p>
                    <p className="font-medium text-foreground" data-testid="review-targeting-geo">
                      {reviewGeo.length > 0 ? reviewGeo.join(", ") : "Canada (default)"}
                    </p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-3 space-y-0.5">
                    <p className="text-muted-foreground">Age Range</p>
                    <p className="font-medium text-foreground" data-testid="review-targeting-age">{reviewAgeMin}–{reviewAgeMax}</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-3 space-y-0.5">
                    <p className="text-muted-foreground">Interests</p>
                    <p className="font-medium text-foreground truncate" data-testid="review-targeting-interests">
                      {reviewInterests.length > 0 ? reviewInterests.join(", ") : "Broad (no filter)"}
                    </p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-3 space-y-0.5">
                    <p className="text-muted-foreground">Daily Budget</p>
                    <p className="font-medium text-foreground" data-testid="review-budget">${(reviewCampaign as any).dailyBudget || reviewCampaign.budget || 10}/day</p>
                  </div>
                </div>
              </div>

              {launchBlockers.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-400 mb-1">Cannot launch — fix these issues:</p>
                  <ul className="text-xs text-red-300/80 space-y-0.5 list-disc list-inside" data-testid="launch-blockers">
                    {launchBlockers.map((b, i) => (
                      <li key={i}>
                        {b}
                        {b.includes("Meta Ads account") && (
                          <a href="/settings?tab=integrations" className="ml-1 text-blue-400 underline hover:text-blue-300" data-testid="link-connect-meta-settings">Connect Meta → Settings</a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Launch mode */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Launch Mode</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="button-launch-active"
                    onClick={() => setLaunchAsPaused(false)}
                    className={`rounded-lg border p-3 text-left transition-all ${!launchAsPaused ? "border-emerald-500 bg-emerald-500/10" : "border-border bg-muted/20 hover:border-border/70"}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Radio size={13} className={!launchAsPaused ? "text-emerald-400" : "text-muted-foreground"} />
                      <span className="text-xs font-semibold text-foreground">Launch Active</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Recommended — goes live immediately</p>
                  </button>
                  <button
                    data-testid="button-launch-paused"
                    onClick={() => setLaunchAsPaused(true)}
                    className={`rounded-lg border p-3 text-left transition-all ${launchAsPaused ? "border-amber-500 bg-amber-500/10" : "border-border bg-muted/20 hover:border-border/70"}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Pause size={13} className={launchAsPaused ? "text-amber-400" : "text-muted-foreground"} />
                      <span className="text-xs font-semibold text-foreground">Test Launch (Paused)</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Creates on Meta in paused state for review</p>
                  </button>
                </div>
              </div>

              {/* Streaming launch log */}
              {launchLog.length > 0 && (
                <div ref={launchLogRef} className="rounded-lg bg-muted/30 border border-border p-3 space-y-1 max-h-48 overflow-y-auto" data-testid="launch-log">
                  {launchLog.map((line, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      {line.startsWith("❌") ? (
                        <AlertCircle size={11} className="text-red-400 shrink-0" />
                      ) : (
                        <Loader2 size={11} className={`shrink-0 ${isLaunchingThis && i === launchLog.length - 1 ? "animate-spin text-emerald-400" : "text-emerald-500/50"}`} />
                      )}
                      <span className={`text-[11px] ${i === launchLog.length - 1 ? "text-foreground" : "text-muted-foreground/60"}`}>{line}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="px-6 pb-6 pt-4 border-t border-border flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setReviewCampaign(null)}
                className="flex-1 border-border text-muted-foreground"
                data-testid="button-cancel-launch"
                disabled={isLaunchingThis}
              >
                Cancel
              </Button>
              <Button
                onClick={() => approveLaunch.mutate({ campaignId: reviewCampaign.id, asPaused: launchAsPaused })}
                disabled={isLaunchingThis || !canLaunch}
                className={`flex-1 gap-2 font-semibold ${!canLaunch ? "bg-muted text-muted-foreground cursor-not-allowed" : launchAsPaused ? "bg-amber-600 hover:bg-amber-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}
                data-testid="button-confirm-launch"
              >
                {isLaunchingThis ? (
                  <><Loader2 size={14} className="animate-spin" /> Launching...</>
                ) : (
                  <><Rocket size={14} />{launchAsPaused ? "Save to Meta (Paused)" : "Launch on Meta →"}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* T003: Ask Pete Dialog */}
    {showAskPeteDialog && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ask-pete-dialog">
        <Card className="w-full max-w-lg bg-card border-amber-500/20">
          <CardHeader className="flex flex-row items-start justify-between pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><Bot size={18} className="text-amber-400" /></div>
              <div>
                <CardTitle className="text-base font-bold text-foreground">Ask Pete</CardTitle>
                <p className="text-xs text-muted-foreground/70 mt-0.5">{peteRunning ? "Pete is working on your request…" : "Describe your campaign goal and Pete will build it from your strategy"}</p>
              </div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setShowAskPeteDialog(false)} data-testid="button-close-ask-pete" title={peteRunning ? "Close dialog — Pete continues working in the background" : "Close"}><X size={16} /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {peteRunning ? (
              <div className="space-y-3" data-testid="pete-stream-log">
                <div className="bg-muted/30 border border-border rounded-lg p-3 max-h-60 overflow-y-auto font-mono text-[11px] text-foreground/80 space-y-1">
                  {peteStreamLog.length === 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Initializing…</div>
                  )}
                  {peteStreamLog.map((msg, i) => (
                    <div key={i} className={`${msg.startsWith("✓") ? "text-emerald-400" : msg.startsWith("❌") ? "text-red-400" : ""}`}>{msg}</div>
                  ))}
                  {peteRunning && <div className="flex items-center gap-2 text-amber-400 mt-1"><Loader2 size={10} className="animate-spin" /> Working…</div>}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { setShowAskPeteDialog(false); }}
                  data-testid="button-minimize-pete"
                >
                  Minimize (Pete continues in background)
                </Button>
              </div>
            ) : (
              <>
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "Draft campaigns from strategy", prompt: "Draft 2-3 campaigns from my marketing strategy — use my target audience, budget, and top priorities." },
                { label: "Check performance & optimize", prompt: "Review my active campaigns and suggest optimizations for budget allocation, targeting, and creative." },
                { label: "Generate 3 ad variants", prompt: "Generate 3 high-converting ad copy variants for my best-performing campaign objective." },
                { label: "Optimize — pause underperformers", prompt: "Analyze my active campaigns, pause any underperformers, and reallocate budget to top-performing ads." },
              ].map(({ label, prompt }) => (
                <button
                  key={label}
                  data-testid={`button-pete-preset-${label.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 30)}`}
                  onClick={() => setAskPeteBrief(prompt)}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="input-ask-pete-brief"
              placeholder="e.g. Generate campaigns to capture spring renovation leads in the Greater Toronto Area, budget $50/day, focus on homeowners 35-55..."
              className="min-h-[120px] bg-muted/30 border-border resize-none"
              value={askPeteBrief}
              onChange={(e) => setAskPeteBrief(e.target.value)}
            />
            {askPeteBrief.length > 0 && askPeteBrief.trim().length < 10 && (
              <p className="text-xs text-muted-foreground/60">Minimum 10 characters ({10 - askPeteBrief.trim().length} more needed)</p>
            )}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowAskPeteDialog(false)}>Cancel</Button>
              <Button
                data-testid="button-ask-pete-submit"
                className="flex-1 gap-2 bg-amber-600 hover:bg-amber-500 text-white"
                disabled={peteRunning || askPeteBrief.trim().length < 10}
                onClick={async () => {
                  if (!activeWorkspaceId || askPeteBrief.trim().length < 10) return;
                  setPeteRunning(true);
                  setPeteStreamLog(["Sending brief to Pete…"]);
                  try {
                    await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/orchestrate-pete`, { task: askPeteBrief });
                    setPeteStreamLog(prev => [...prev, "Pete received the request — building campaigns…"]);
                  } catch (e: any) {
                    const msg: string = e?.message ?? "";
                    if (msg.includes("already active") || msg.startsWith("429")) {
                      setPeteStreamLog(prev => [...prev, "Pete is already working on a task — watch for updates"]);
                    } else {
                      setPeteRunning(false);
                      setPeteStreamLog([]);
                      toast({ title: "Could not reach Pete", description: msg, variant: "destructive" });
                    }
                  }
                }}
              >
                {peteRunning ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
                Send to Pete
              </Button>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    )}

    {/* T003: Creative Studio Tab Content */}
    {activeTab === "creative" && (
      <Card className="bg-gradient-to-br from-purple-950/30 to-transparent border-purple-500/15" data-testid="creative-studio-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10"><Sparkles size={18} className="text-purple-400" /></div>
            <div>
              <CardTitle className="text-base font-bold text-foreground">Pete's Creative Studio</CardTitle>
              <p className="text-xs text-muted-foreground/70 mt-0.5">Generate ad headlines, body copy, CTAs, and carousel slides — instantly</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Creative Brief</label>
            <Textarea
              data-testid="input-creative-brief"
              placeholder="What product or offer are you advertising? Who is the audience? What's the main benefit or hook?"
              className="min-h-[100px] bg-muted/30 border-border resize-none"
              value={creativeBrief}
              onChange={(e) => setCreativeBrief(e.target.value)}
            />
          </div>
          <div className="flex gap-4 flex-wrap">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Platform</label>
              <div className="flex gap-1 p-0.5 rounded-lg bg-muted/40 border border-border">
                {(["Meta", "LinkedIn", "Google", "X"] as const).map(p => (
                  <button
                    key={p}
                    data-testid={`button-creative-platform-${p.toLowerCase()}`}
                    onClick={() => setCreativePlatform(p)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${creativePlatform === p ? "bg-purple-600/30 text-purple-300 border border-purple-500/30" : "text-muted-foreground hover:text-foreground"}`}
                  >{p}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Format</label>
              <div className="flex gap-1 p-0.5 rounded-lg bg-muted/40 border border-border">
                {(["single", "carousel", "video_script"] as const).map(f => (
                  <button
                    key={f}
                    data-testid={`button-creative-format-${f}`}
                    onClick={() => setCreativeFormat(f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${creativeFormat === f ? "bg-purple-600/30 text-purple-300 border border-purple-500/30" : "text-muted-foreground hover:text-foreground"}`}
                  >{f === "video_script" ? "Video Script" : f.charAt(0).toUpperCase() + f.slice(1)}</button>
                ))}
              </div>
            </div>
            {creativeFormat === "carousel" && (
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Slides: {creativeSlideCount}</label>
                <input
                  data-testid="input-creative-slide-count"
                  type="range" min={3} max={8} step={1}
                  value={creativeSlideCount}
                  onChange={(e) => setCreativeSlideCount(Number(e.target.value))}
                  className="w-32 accent-purple-500"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground/60 w-32"><span>3</span><span>8</span></div>
              </div>
            )}
          </div>
          <Button
            data-testid="button-generate-creative"
            disabled={creativeGenerating || !creativeBrief.trim()}
            className="gap-2 bg-purple-600 hover:bg-purple-500 text-white"
            onClick={async () => {
              if (!activeWorkspaceId || !creativeBrief.trim()) return;
              setCreativeGenerating(true);
              setCreativeOutput(null);
              try {
                const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/pete/generate-creative`, {
                  brief: creativeBrief,
                  format: creativeFormat,
                  slideCount: creativeSlideCount,
                  platform: creativePlatform,
                });
                const data = await res.json();
                if (data.message) throw new Error(data.message);
                setCreativeOutput(data);
              } catch (e: any) {
                toast({ title: "Creative generation failed", description: e.message, variant: "destructive" });
              } finally {
                setCreativeGenerating(false);
              }
            }}
          >
            {creativeGenerating ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate Creative</>}
          </Button>

          {creativeOutput && (
            <div className="space-y-4 animate-in fade-in duration-300" data-testid="creative-output">
              {creativeOutput.headlines?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Headlines</p>
                  <div className="space-y-1.5">
                    {creativeOutput.headlines.map((h, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-muted/40 border border-border text-sm font-medium text-foreground flex items-center justify-between gap-2" data-testid={`creative-headline-${i}`}>
                        <span>{h}</span>
                        <button data-testid={`button-copy-headline-${i}`} className="shrink-0 p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors" onClick={() => { navigator.clipboard.writeText(h); toast({ title: "Copied!" }); }}><Copy size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {creativeOutput.bodyCopy?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Body Copy</p>
                  <div className="space-y-1.5">
                    {creativeOutput.bodyCopy.map((b, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-muted/40 border border-border text-sm text-muted-foreground flex items-start justify-between gap-2" data-testid={`creative-body-${i}`}>
                        <span>{b}</span>
                        <button data-testid={`button-copy-body-${i}`} className="shrink-0 p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors mt-0.5" onClick={() => { navigator.clipboard.writeText(b); toast({ title: "Copied!" }); }}><Copy size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {creativeOutput.ctas?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Call to Action</p>
                  <div className="flex flex-wrap gap-2">
                    {creativeOutput.ctas.map((c, i) => (
                      <Badge key={i} className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 cursor-pointer hover:bg-emerald-500/25 transition-colors" data-testid={`creative-cta-${i}`} onClick={() => { navigator.clipboard.writeText(c); toast({ title: "Copied!" }); }}>{c} <Copy size={10} className="ml-1 opacity-50" /></Badge>
                    ))}
                  </div>
                </div>
              )}
              {creativeOutput.slides?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Carousel Slides</p>
                  <div className="space-y-2">
                    {creativeOutput.slides.map((s, i) => (
                      <div key={i} className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/15" data-testid={`creative-slide-${i}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-amber-400/70 font-semibold mb-1">Slide {s.slide}</p>
                          <button data-testid={`button-copy-slide-${i}`} className="shrink-0 p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors" onClick={() => { navigator.clipboard.writeText(`${s.headline}\n${s.body}`); toast({ title: "Copied!" }); }}><Copy size={13} /></button>
                        </div>
                        <p className="text-sm font-bold text-foreground">{s.headline}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {creativeOutput.notes && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border">
                  <p className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider mb-1">Pete's Notes</p>
                  <p className="text-xs text-muted-foreground">{creativeOutput.notes}</p>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="button-save-creative-draft"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-purple-500/30 text-purple-300 hover:bg-purple-500/10 text-xs"
                  onClick={async () => {
                    if (!activeWorkspaceId || !creativeOutput) return;
                    try {
                      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/pete/save-creative-draft`, {
                        brief: creativeBrief,
                        format: creativeFormat,
                        ...creativeOutput,
                      });
                      const data = await res.json();
                      if (data.message) throw new Error(data.message);
                      toast({ title: "Saved as Draft", description: `"${data.name}" added to your campaigns list.` });
                      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"] });
                    } catch (e: any) {
                      toast({ title: "Save failed", description: e.message, variant: "destructive" });
                    }
                  }}
                >
                  Save as Draft Campaign
                </Button>
                {creativeFormat === "video_script" && (
                  <Button
                    data-testid="button-generate-video-ad"
                    size="sm"
                    variant="outline"
                    className="gap-1.5 border-violet-500/30 text-violet-300 hover:bg-violet-500/10 text-xs"
                    disabled={videoAdGenerating || videoAdStatus === "rendering"}
                    onClick={async () => {
                      if (!activeWorkspaceId || !creativeOutput) return;
                      const scriptParts = [
                        ...(creativeOutput.headlines || []),
                        ...(creativeOutput.bodyCopy || []),
                      ];
                      const fullScript = scriptParts.join("\n\n");
                      setVideoAdPanel(true);
                      setVideoAdGenerating(true);
                      setVideoAdError(null);
                      setVideoAdMp4(null);
                      setVideoAdId(null);
                      setVideoAdStatus(null);

                      try {
                        const scriptRes = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/heygen/generate-script`, {
                          brief: creativeBrief || fullScript,
                          platform: "social",
                          duration: 30,
                        });
                        const scriptData = await scriptRes.json();
                        const finalScript = scriptData.script || fullScript;

                        const avatarRes = await fetch(`/api/workspaces/${activeWorkspaceId}/heygen/avatars`, { credentials: "include" });
                        if (avatarRes.status === 422) {
                          setVideoAdError("HEYGEN_API_KEY not configured. Add it in Settings to enable video ads.");
                          setVideoAdGenerating(false);
                          return;
                        }
                        const avatarData = await avatarRes.json();
                        const avatars = Array.isArray(avatarData) ? avatarData : [];
                        const defaultAvatar = avatars[0]?.avatar_id;

                        if (!defaultAvatar) {
                          setVideoAdError("No HeyGen avatars available. Configure HEYGEN_API_KEY first.");
                          setVideoAdGenerating(false);
                          return;
                        }

                        const genRes = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/heygen/generate`, {
                          avatarId: defaultAvatar,
                          script: finalScript,
                        });
                        const genData = await genRes.json();
                        setVideoAdId(genData.videoId);
                        setVideoAdStatus("rendering");
                        setVideoAdGenerating(false);

                        const pollInterval = setInterval(async () => {
                          try {
                            const statusRes = await fetch(`/api/workspaces/${activeWorkspaceId}/heygen/status/${genData.videoId}`, { credentials: "include" });
                            const statusData = await statusRes.json();
                            setVideoAdStatus(statusData.status);
                            if (statusData.status === "completed") {
                              setVideoAdMp4(statusData.mp4Url || null);
                              clearInterval(pollInterval);
                            } else if (statusData.status === "failed") {
                              setVideoAdError(statusData.error || "Video generation failed");
                              clearInterval(pollInterval);
                            }
                          } catch {
                            clearInterval(pollInterval);
                            setVideoAdError("Failed to check video status");
                          }
                        }, 10_000);

                        setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
                      } catch (e: unknown) {
                        const msg = e instanceof Error ? e.message : String(e);
                        setVideoAdError(msg);
                        setVideoAdGenerating(false);
                      }
                    }}
                  >
                    {videoAdGenerating ? (
                      <><Loader2 size={12} className="animate-spin" /> Generating Video Ad…</>
                    ) : videoAdStatus === "rendering" ? (
                      <><Loader2 size={12} className="animate-spin" /> Rendering…</>
                    ) : (
                      <><Video size={12} /> Generate Video Ad</>
                    )}
                  </Button>
                )}
                {creativeFormat === "video_script" && (
                  <Button
                    data-testid="button-send-to-heygen"
                    size="sm"
                    variant="outline"
                    className="gap-1.5 border-purple-500/20 text-muted-foreground hover:text-foreground text-xs"
                    onClick={() => {
                      const scriptParts = [
                        ...(creativeOutput.headlines || []),
                        ...(creativeOutput.bodyCopy || []),
                      ];
                      const fullScript = scriptParts.join("\n\n");
                      const params = new URLSearchParams({ script: fullScript, brief: creativeBrief });
                      window.location.href = studioHref("videos", Object.fromEntries(params.entries()));
                    }}
                  >
                    <ExternalLink size={12} /> Open in Video Studio
                  </Button>
                )}
              </div>
              {videoAdPanel && (
                <div className="mt-4 rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 space-y-3" data-testid="video-ad-panel">
                  <div className="flex items-center gap-2">
                    <Video size={14} className="text-violet-400" />
                    <span className="text-sm font-semibold text-violet-300">Video Ad Generation</span>
                  </div>
                  {videoAdGenerating && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 size={14} className="animate-spin text-violet-400" />
                      Generating script and starting video render…
                    </div>
                  )}
                  {videoAdStatus === "rendering" && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-yellow-300">
                        <Loader2 size={14} className="animate-spin" />
                        Rendering video (this takes 30–120 seconds)…
                      </div>
                      <div className="w-full bg-muted/40 rounded-full h-1.5">
                        <div className="bg-yellow-400 h-1.5 rounded-full animate-pulse w-2/3" />
                      </div>
                      {videoAdId && <p className="text-xs text-muted-foreground">Video ID: {videoAdId}</p>}
                    </div>
                  )}
                  {videoAdStatus === "completed" && videoAdMp4 && (
                    <div className="space-y-2">
                      <p className="text-sm text-green-400 font-medium">Video ad ready!</p>
                      <video src={videoAdMp4} controls className="w-full max-w-md rounded-lg border border-border" data-testid="video-ad-preview" />
                      <div className="flex gap-2">
                        <Button
                          data-testid="button-save-video-creative"
                          size="sm"
                          variant="outline"
                          className="gap-1.5 border-green-500/30 text-green-300 hover:bg-green-500/10 text-xs"
                          onClick={async () => {
                            if (!activeWorkspaceId || !creativeOutput) return;
                            try {
                              const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/pete/save-creative-draft`, {
                                brief: creativeBrief,
                                format: "video_script",
                                ...creativeOutput,
                                videoUrl: videoAdMp4,
                              });
                              const data = await res.json();
                              toast({ title: "Video Ad Saved", description: `"${data.name}" saved as draft campaign with video asset.` });
                              queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"] });
                            } catch (e: unknown) {
                              const msg = e instanceof Error ? e.message : String(e);
                              toast({ title: "Save failed", description: msg, variant: "destructive" });
                            }
                          }}
                        >
                          <Save size={12} /> Save as Campaign
                        </Button>
                      </div>
                    </div>
                  )}
                  {videoAdError && (
                    <div className="text-sm text-red-400" data-testid="video-ad-error">
                      {videoAdError}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    )}

    {/* Delete Confirmation Dialog for Meta Campaigns */}
    {deleteConfirmCampaign && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="delete-confirm-dialog"
        onClick={(e) => { if (e.target === e.currentTarget) setDeleteConfirmCampaign(null); }}
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10"><AlertCircle size={18} className="text-red-400" /></div>
            <div>
              <h2 className="font-semibold text-foreground text-base">Delete Meta-Linked Campaign</h2>
              <p className="text-xs text-muted-foreground/70 mt-0.5">This campaign is linked to Meta Ads ({deleteConfirmCampaign.status === "ACTIVE" ? "currently active" : "currently " + (deleteConfirmCampaign.status || "paused").toLowerCase()})</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{deleteConfirmCampaign.name}</span> is linked to Meta Campaign ID ({(deleteConfirmCampaign as any).metaCampaignId}). Deleting here removes the local record only — it does <span className="font-semibold text-red-400">not</span> stop, pause, or delete the campaign on Meta. Manage it directly in Meta Ads Manager.
          </p>
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setDeleteConfirmCampaign(null)}
              data-testid="button-delete-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1 gap-2"
              data-testid="button-delete-confirm-proceed"
              onClick={() => {
                deleteCampaign.mutate(deleteConfirmCampaign.id, {
                  onSuccess: () => setDeleteConfirmCampaign(null),
                  onError: (err: any) => {
                    toast({ title: "Delete Failed", description: err.message, variant: "destructive" });
                    setDeleteConfirmCampaign(null);
                  },
                });
              }}
              disabled={deleteCampaign.isPending}
            >
              {deleteCampaign.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete Local Record
            </Button>
          </div>
          <a
            href={buildAdsManagerUrl((deleteConfirmCampaign as any).metaAdAccountId || metaAdAccountId || "", (deleteConfirmCampaign as any).metaCampaignId)}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
            data-testid="link-open-meta-manager-delete"
          >
            Open in Meta Ads Manager first ↗
          </a>
        </div>
      </div>
    )}
    </>
  );
}